import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { client } from '../api/client';
import { useAuthStore } from '../store/authStore';
import DashboardSection from '../components/admin/DashboardSection';
import CustomersSection from '../components/admin/CustomersSection';
import LeadsSection from '../components/admin/LeadsSection';
import PaymentsSection from '../components/admin/PaymentsSection';
import PackagesSection from '../components/admin/PackagesSection';
import WhatsAppTemplatesSection from '../components/admin/WhatsAppTemplatesSection';
import TranslationsSection from '../components/admin/TranslationsSection';
import { formatDate, formatDateTime } from '../lib/localized';
import {
  Anchor,
  Compass,
  Fish,
  Heart,
  MapPin,
  Mountain,
  Palmtree,
  Sailboat,
  Shell,
  Ship,
  Star,
  Sun,
  Sunset,
  TreePine,
  Users,
  Waves,
  Zap,
} from 'lucide-react';

function ImagePreview({ url, className = '' }: { url: string | null | undefined; className?: string }) {
  const { t } = useTranslation('admin');
  if (!url || !url.trim()) return null;
  return (
    <div className={`mt-1 ${className}`}>
      <img
        src={url}
        alt={t('common.preview')}
        className="w-16 h-16 object-cover rounded-lg border border-gray-200 bg-gray-50"
        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
      />
    </div>
  );
}

interface AdminAccount {
  id: number;
  userId: string;
  email: string;
  role: string;
  createdAt: number;
}

interface Experience {
  id: number;
  name: string;
  nameZh: string;
  nameZhCn: string | null;
  slug: string;
  description: string | null;
  descriptionZh: string | null;
  descriptionZhCn: string | null;
  duration: string | null;
  groupSize: string | null;
  includes: string | null;
  price: number | null;
  priceNote: string | null;
  imageUrl: string | null;
  iconName: string | null;
  sortOrder: number;
  status: 'active' | 'inactive';
  createdAt: number;
  updatedAt: number;
}

interface Retreat {
  id: number;
  name: string;
  nameZh: string;
  slug: string;
  description: string | null;
  descriptionZh: string | null;
  duration: string | null;
  location: string | null;
  audience: string | null;
  itinerary: string | null;
  priceNote: string | null;
  imageUrl: string | null;
  iconName: string | null;
  sortOrder: number;
  status: 'active' | 'inactive';
  createdAt: number;
  updatedAt: number;
}

interface Property {
  id: number;
  name: string;
  nameZh: string;
  nameZhCn: string | null;
  description: string | null;
  descriptionZh: string | null;
  descriptionZhCn: string | null;
  location: string | null;
  pricePerNight: number;
  maxGuests: number | null;
  imageUrl: string | null;
  gallery: string | null;
  facilities: string | null;
  locationDetails: string | null;
  story: string | null;
  status: 'active' | 'inactive' | 'draft';
  createdAt: number;
  updatedAt: number;
}

interface RoomType {
  id: number;
  propertyId: number;
  name: string;
  nameZh: string;
  nameZhCn: string | null;
  description: string | null;
  descriptionZh: string | null;
  descriptionZhCn: string | null;
  pricePerNight: number;
  maxGuests: number | null;
  inventory: number;
  imageUrl: string | null;
  amenities: string | null;
  bedType: string | null;
  view: string | null;
  sizeSqm: number | null;
  occupancy: string | null;
  gallery: string | null;
  features: string | null;
  status: 'available' | 'unavailable' | 'hidden';
  createdAt: number;
  updatedAt: number;
}

interface ExperienceFormState {
  name: string;
  nameZh: string;
  nameZhCn: string;
  slug: string;
  description: string;
  descriptionZh: string;
  descriptionZhCn: string;
  duration: string;
  groupSize: string;
  includesText: string;
  price: string;
  priceNote: string;
  imageUrl: string;
  iconName: string;
  sortOrder: string;
  status: 'active' | 'inactive';
}

interface RetreatFormState {
  name: string;
  nameZh: string;
  slug: string;
  description: string;
  descriptionZh: string;
  duration: string;
  location: string;
  audience: string;
  itinerary: { day: string; title: string; desc: string }[];
  priceNote: string;
  imageUrl: string;
  iconName: string;
  sortOrder: string;
  status: 'active' | 'inactive';
}

interface FacilityItem {
  icon: string;
  label: string;
}

interface LocationDetailsShape {
  description: string;
  mapImage: string;
  nearby: string[];
}

interface StoryShape {
  title: string;
  content: string;
}

interface PropertyFormState {
  name: string;
  nameZh: string;
  nameZhCn: string;
  description: string;
  descriptionZh: string;
  descriptionZhCn: string;
  location: string;
  pricePerNight: string;
  maxGuests: string;
  imageUrl: string;
  status: 'active' | 'inactive' | 'draft';
  gallery: string[];
  facilities: FacilityItem[];
  locationDetails: LocationDetailsShape;
  story: StoryShape;
}

interface RoomTypeFormState {
  name: string;
  nameZh: string;
  nameZhCn: string;
  description: string;
  descriptionZh: string;
  descriptionZhCn: string;
  pricePerNight: string;
  maxGuests: string;
  inventory: string;
  imageUrl: string;
  amenitiesText: string;
  bedType: string;
  view: string;
  sizeSqm: string;
  occupancy: string;
  gallery: string[];
  featuresText: string;
  status: 'available' | 'unavailable' | 'hidden';
}

function safeJsonParse<T>(value: string | null | undefined, fallback: T): T {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

function linesToArray(text: string): string[] {
  return text
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

function arrayToLines(arr: string[] | null | undefined): string {
  return (arr || []).join('\n');
}

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Anchor,
  Compass,
  Fish,
  Heart,
  MapPin,
  Mountain,
  Palmtree,
  Sailboat,
  Shell,
  Ship,
  Star,
  Sun,
  Sunset,
  TreePine,
  Users,
  Waves,
  Zap,
};

const ICON_NAMES = Object.keys(ICONS);

function IconPreview({ name }: { name: string | null | undefined }) {
  const Comp = ICONS[name || ''] || Compass;
  return <Comp className="w-5 h-5" />;
}

function statusBadgeClass(status: string) {
  switch (status) {
    case 'active':
    case 'available':
      return 'bg-green-100 text-green-700';
    case 'inactive':
    case 'unavailable':
      return 'bg-red-100 text-red-700';
    case 'draft':
    case 'hidden':
      return 'bg-gray-100 text-gray-700';
    default:
      return 'bg-yellow-100 text-yellow-700';
  }
}

// ============================================================================
// Experiences Section
// ============================================================================

function ExperiencesSection() {
  const { t } = useTranslation('admin');
  const [experiences, setExperiences] = useState<Experience[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const emptyForm = (): ExperienceFormState => ({
    name: '',
    nameZh: '',
    nameZhCn: '',
    slug: '',
    description: '',
    descriptionZh: '',
    descriptionZhCn: '',
    duration: '',
    groupSize: '',
    includesText: '',
    price: '',
    priceNote: '',
    imageUrl: '',
    iconName: 'Compass',
    sortOrder: '0',
    status: 'active',
  });

  const [form, setForm] = useState<ExperienceFormState>(emptyForm());

  async function load() {
    setLoading(true);
    setError('');
    try {
      const res = await client.api.fetch('/api/admin/experiences');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('common.loadFailed'));
      setExperiences(data.data || []);
    } catch (err: any) {
      setError(err.message || t('experiences.loadError'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function openNew() {
    setEditingId(null);
    setForm(emptyForm());
    setIsFormOpen(true);
    setError('');
    setSuccess('');
    setDeleteConfirmId(null);
  }

  function startEdit(item: Experience) {
    setEditingId(item.id);
    setForm({
      name: item.name || '',
      nameZh: item.nameZh || '',
      nameZhCn: item.nameZhCn || '',
      slug: item.slug || '',
      description: item.description || '',
      descriptionZh: item.descriptionZh || '',
      descriptionZhCn: item.descriptionZhCn || '',
      duration: item.duration || '',
      groupSize: item.groupSize || '',
      includesText: arrayToLines(safeJsonParse<string[]>(item.includes, [])),
      price: item.price != null ? String(item.price) : '',
      priceNote: item.priceNote || '',
      imageUrl: item.imageUrl || '',
      iconName: item.iconName || 'Compass',
      sortOrder: String(item.sortOrder ?? 0),
      status: item.status,
    });
    setIsFormOpen(true);
    setError('');
    setSuccess('');
    setDeleteConfirmId(null);
  }

  function closeForm() {
    setIsFormOpen(false);
    setEditingId(null);
    setForm(emptyForm());
  }

  async function save() {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const body = {
        name: form.name.trim() || null,
        nameZh: form.nameZh.trim(),
        nameZhCn: form.nameZhCn.trim() || null,
        slug: form.slug.trim(),
        description: form.description.trim() || null,
        descriptionZh: form.descriptionZh.trim() || null,
        descriptionZhCn: form.descriptionZhCn.trim() || null,
        duration: form.duration.trim() || null,
        groupSize: form.groupSize.trim() || null,
        includes: linesToArray(form.includesText),
        price: form.price === '' ? null : Number(form.price),
        priceNote: form.priceNote.trim() || null,
        imageUrl: form.imageUrl.trim() || null,
        iconName: form.iconName.trim() || null,
        sortOrder: Number(form.sortOrder) || 0,
        status: form.status,
      };

      if (!body.nameZh || !body.slug) {
        throw new Error(t('experiences.validation'));
      }

      let res: Response;
      if (editingId) {
        res = await client.api.fetch(`/api/admin/experiences/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      } else {
        res = await client.api.fetch('/api/admin/experiences', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('common.saveFailed'));

      setSuccess(editingId ? t('experiences.updateSuccess') : t('experiences.createSuccess'));
      closeForm();
      await load();
    } catch (err: any) {
      setError(err.message || t('common.saveErrorGeneric'));
    } finally {
      setLoading(false);
    }
  }

  async function confirmDelete(id: number) {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await client.api.fetch(`/api/admin/experiences/${id}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('common.deleteFailed'));
      setSuccess(t('experiences.deleteSuccess'));
      await load();
    } catch (err: any) {
      setError(err.message || t('common.deleteErrorGeneric'));
    } finally {
      setLoading(false);
      setDeleteConfirmId(null);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg text-[#0d1b2a]">{t('experiences.title')}</h3>
        <button
          onClick={openNew}
          className="bg-[#0a4c6b] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#083d56] transition"
        >
          {t('experiences.addExperience')}
        </button>
      </div>

      {error && (
        <p className="text-sm text-red-600 bg-red-50 px-4 py-3 rounded-lg">{error}</p>
      )}
      {success && (
        <p className="text-sm text-green-600 bg-green-50 px-4 py-3 rounded-lg">{success}</p>
      )}

      {isFormOpen && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <h4 className="font-semibold text-[#0d1b2a] mb-4">
            {editingId ? t('experiences.editExperience') : t('experiences.addExperience')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('common.nameEn')}</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('common.nameZh')}</label>
              <input
                type="text"
                value={form.nameZh}
                onChange={(e) => setForm({ ...form, nameZh: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('common.nameZhCn')}</label>
              <input
                type="text"
                value={form.nameZhCn}
                onChange={(e) => setForm({ ...form, nameZhCn: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Slug</label>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('common.duration')}</label>
              <input
                type="text"
                value={form.duration}
                onChange={(e) => setForm({ ...form, duration: e.target.value })}
                placeholder={t('experiences.durationPlaceholder')}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('experiences.groupSize')}</label>
              <input
                type="text"
                value={form.groupSize}
                onChange={(e) => setForm({ ...form, groupSize: e.target.value })}
                placeholder={t('experiences.groupSizePlaceholder')}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('common.priceHkd')}</label>
              <input
                type="number"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                placeholder={t('experiences.pricePlaceholder')}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('experiences.priceNote')}</label>
              <input
                type="text"
                value={form.priceNote}
                onChange={(e) => setForm({ ...form, priceNote: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('common.imageUrl')}</label>
              <input
                type="text"
                value={form.imageUrl}
                onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
              <ImagePreview url={form.imageUrl} />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('experiences.icon')}</label>
              <div className="flex items-center gap-2">
                <select
                  value={form.iconName}
                  onChange={(e) => setForm({ ...form, iconName: e.target.value })}
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                >
                  {ICON_NAMES.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <div className="text-[#0a4c6b]">
                  <IconPreview name={form.iconName} />
                </div>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('common.sortOrder')}</label>
              <input
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('common.status')}</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as 'active' | 'inactive' })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              >
                <option value="active">{t('common.statusActive')}</option>
                <option value="inactive">{t('common.statusInactive')}</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">{t('common.descriptionEn')}</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">{t('common.descriptionZh')}</label>
              <textarea
                value={form.descriptionZh}
                onChange={(e) => setForm({ ...form, descriptionZh: e.target.value })}
                rows={3}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">{t('common.descriptionZhCn')}</label>
              <textarea
                value={form.descriptionZhCn}
                onChange={(e) => setForm({ ...form, descriptionZhCn: e.target.value })}
                rows={3}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">
                {t('experiences.includesLabel')}
              </label>
              <textarea
                value={form.includesText}
                onChange={(e) => setForm({ ...form, includesText: e.target.value })}
                rows={4}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={save}
              disabled={loading}
              className="bg-[#0a4c6b] text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-[#083d56] transition disabled:opacity-60"
            >
              {loading ? t('common.saving') : editingId ? t('experiences.updateExperience') : t('experiences.addExperience')}
            </button>
            <button
              onClick={closeForm}
              className="text-sm text-gray-500 hover:text-gray-700 px-3 py-2"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">{t('common.nameZh')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">{t('common.duration')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">{t('common.price')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">{t('common.status')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">{t('common.sortOrder')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {experiences.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                    {t('experiences.empty')}
                  </td>
                </tr>
              ) : (
                experiences.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-[#0d1b2a]">
                      <div className="flex items-center gap-2">
                        <span className="text-[#0a4c6b]">
                          <IconPreview name={item.iconName} />
                        </span>
                        {item.nameZh}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{item.duration || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {item.price != null ? `HK$${item.price.toLocaleString()}` : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(item.status)}`}>
                        {item.status === 'active' ? t('common.statusActive') : t('common.statusInactive')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{item.sortOrder}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => startEdit(item)}
                          className="text-xs text-[#0a4c6b] hover:underline"
                        >
                          {t('common.edit')}
                        </button>
                        {deleteConfirmId === item.id ? (
                          <>
                            <button
                              onClick={() => confirmDelete(item.id)}
                              className="text-xs bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                            >
                              {t('common.confirm')}
                            </button>
                            <button
                              onClick={() => setDeleteConfirmId(null)}
                              className="text-xs text-gray-500 hover:text-gray-700"
                            >
                              {t('common.cancel')}
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirmId(item.id)}
                            className="text-xs text-red-500 hover:text-red-700"
                          >
                            {t('common.delete')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Properties Section
// ============================================================================

function PropertiesSection() {
  const { t } = useTranslation('admin');
  const [properties, setProperties] = useState<Property[]>([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | 'new' | null>(null);
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteConfirmPropertyId, setDeleteConfirmPropertyId] = useState<number | null>(null);
  const [deleteConfirmRoomTypeId, setDeleteConfirmRoomTypeId] = useState<number | null>(null);

  const [editingRoomTypeId, setEditingRoomTypeId] = useState<number | 'new' | null>(null);

  const emptyPropertyForm = (): PropertyFormState => ({
    name: '',
    nameZh: '',
    nameZhCn: '',
    description: '',
    descriptionZh: '',
    descriptionZhCn: '',
    location: '',
    pricePerNight: '',
    maxGuests: '',
    imageUrl: '',
    status: 'draft',
    gallery: [],
    facilities: [],
    locationDetails: { description: '', mapImage: '', nearby: [] },
    story: { title: '', content: '' },
  });

  const [propertyForm, setPropertyForm] = useState<PropertyFormState>(emptyPropertyForm());
  const [allExperiences, setAllExperiences] = useState<Experience[]>([]);
  const [allRetreats, setAllRetreats] = useState<Retreat[]>([]);
  const [selectedExperienceIds, setSelectedExperienceIds] = useState<number[]>([]);
  const [selectedRetreatIds, setSelectedRetreatIds] = useState<number[]>([]);

  const emptyRoomTypeForm = (): RoomTypeFormState => ({
    name: '',
    nameZh: '',
    nameZhCn: '',
    description: '',
    descriptionZh: '',
    descriptionZhCn: '',
    pricePerNight: '',
    maxGuests: '',
    inventory: '1',
    imageUrl: '',
    amenitiesText: '',
    bedType: '',
    view: '',
    sizeSqm: '',
    occupancy: '',
    gallery: [],
    featuresText: '',
    status: 'available',
  });

  const [roomTypeForm, setRoomTypeForm] = useState<RoomTypeFormState>(emptyRoomTypeForm());

  async function loadProperties() {
    setLoading(true);
    setError('');
    try {
      const res = await client.api.fetch('/api/admin/properties');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('common.loadFailed'));
      setProperties(data.data || []);
    } catch (err: any) {
      setError(err.message || t('properties.loadError'));
    } finally {
      setLoading(false);
    }
  }

  async function loadAllExperiencesAndRetreats() {
    try {
      const [expRes, retRes] = await Promise.all([
        client.api.fetch('/api/admin/experiences'),
        client.api.fetch('/api/admin/retreats'),
      ]);
      const expData = await expRes.json();
      const retData = await retRes.json();
      setAllExperiences(expData.data || []);
      setAllRetreats(retData.data || []);
    } catch (err: any) {
      console.error(err);
    }
  }

  async function loadPropertyDetail(id: number) {
    setLoading(true);
    setError('');
    try {
      const [propertyRes, roomTypesRes] = await Promise.all([
        client.api.fetch(`/api/admin/properties/${id}`),
        client.api.fetch(`/api/admin/properties/${id}/room-types`),
      ]);
      const propertyData = await propertyRes.json();
      const roomTypesData = await roomTypesRes.json();
      if (!propertyRes.ok) throw new Error(propertyData.error || t('common.loadFailed'));

      const p: Property & { roomTypes?: RoomType[]; experiences?: Experience[]; retreats?: Retreat[] } = propertyData.data;
      setPropertyForm({
        name: p.name || '',
        nameZh: p.nameZh || '',
        nameZhCn: p.nameZhCn || '',
        description: p.description || '',
        descriptionZh: p.descriptionZh || '',
        descriptionZhCn: p.descriptionZhCn || '',
        location: p.location || '',
        pricePerNight: String(p.pricePerNight ?? ''),
        maxGuests: p.maxGuests != null ? String(p.maxGuests) : '',
        imageUrl: p.imageUrl || '',
        status: p.status,
        gallery: safeJsonParse<string[]>(p.gallery, []),
        facilities: safeJsonParse<FacilityItem[]>(p.facilities, []),
        locationDetails: safeJsonParse<LocationDetailsShape>(p.locationDetails, {
          description: '',
          mapImage: '',
          nearby: [],
        }),
        story: safeJsonParse<StoryShape>(p.story, { title: '', content: '' }),
      });
      setRoomTypes(roomTypesData.data || p.roomTypes || []);
      setSelectedExperienceIds((p.experiences || []).map((e) => e.id));
      setSelectedRetreatIds((p.retreats || []).map((r) => r.id));
    } catch (err: any) {
      setError(err.message || t('properties.detailLoadError'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadProperties();
  }, []);

  useEffect(() => {
    if (selectedPropertyId === 'new') {
      setPropertyForm(emptyPropertyForm());
      setRoomTypes([]);
      setEditingRoomTypeId(null);
      setRoomTypeForm(emptyRoomTypeForm());
      setSelectedExperienceIds([]);
      setSelectedRetreatIds([]);
      loadAllExperiencesAndRetreats();
    } else if (typeof selectedPropertyId === 'number') {
      loadPropertyDetail(selectedPropertyId);
      setEditingRoomTypeId(null);
      setRoomTypeForm(emptyRoomTypeForm());
      loadAllExperiencesAndRetreats();
    }
  }, [selectedPropertyId]);

  function openNewProperty() {
    setSelectedPropertyId('new');
    setError('');
    setSuccess('');
    setDeleteConfirmPropertyId(null);
  }

  function closePropertyEditor() {
    setSelectedPropertyId(null);
    setPropertyForm(emptyPropertyForm());
    setRoomTypes([]);
    setEditingRoomTypeId(null);
  }

  async function saveProperty() {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const body = {
        name: propertyForm.name.trim() || null,
        nameZh: propertyForm.nameZh.trim(),
        nameZhCn: propertyForm.nameZhCn.trim() || null,
        description: propertyForm.description.trim() || null,
        descriptionZh: propertyForm.descriptionZh.trim() || null,
        descriptionZhCn: propertyForm.descriptionZhCn.trim() || null,
        location: propertyForm.location.trim() || null,
        pricePerNight: Number(propertyForm.pricePerNight) || 0,
        maxGuests: propertyForm.maxGuests === '' ? null : Number(propertyForm.maxGuests),
        imageUrl: propertyForm.imageUrl.trim() || null,
        status: propertyForm.status,
        gallery: propertyForm.gallery.filter(Boolean),
        facilities: propertyForm.facilities.filter((f) => f.label.trim()),
        locationDetails: propertyForm.locationDetails,
        story: propertyForm.story,
      };

      if (!body.nameZh) {
        throw new Error(t('properties.validation'));
      }

      let res: Response;
      let savedPropertyId: number | null = null;
      if (selectedPropertyId === 'new') {
        res = await client.api.fetch('/api/admin/properties', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      } else if (typeof selectedPropertyId === 'number') {
        res = await client.api.fetch(`/api/admin/properties/${selectedPropertyId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      } else {
        throw new Error(t('properties.notSelected'));
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('common.saveFailed'));
      savedPropertyId = data.data?.id ?? null;

      // Save linked experiences & retreats
      const propertyIdToLink = savedPropertyId ?? (typeof selectedPropertyId === 'number' ? selectedPropertyId : null);
      if (propertyIdToLink != null) {
        await Promise.all([
          client.api.fetch(`/api/admin/properties/${propertyIdToLink}/experiences`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ experienceIds: selectedExperienceIds }),
          }),
          client.api.fetch(`/api/admin/properties/${propertyIdToLink}/retreats`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ retreatIds: selectedRetreatIds }),
          }),
        ]);
      }

      setSuccess(selectedPropertyId === 'new' ? t('properties.createSuccess') : t('properties.updateSuccess'));
      closePropertyEditor();
      await loadProperties();
    } catch (err: any) {
      setError(err.message || t('common.saveErrorGeneric'));
    } finally {
      setLoading(false);
    }
  }

  async function confirmDeleteProperty(id: number) {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await client.api.fetch(`/api/admin/properties/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('common.deleteFailed'));
      setSuccess(t('properties.deleteSuccess'));
      if (selectedPropertyId === id) closePropertyEditor();
      await loadProperties();
    } catch (err: any) {
      setError(err.message || t('common.deleteErrorGeneric'));
    } finally {
      setLoading(false);
      setDeleteConfirmPropertyId(null);
    }
  }

  // Room type helpers
  function startNewRoomType() {
    setEditingRoomTypeId('new');
    setRoomTypeForm(emptyRoomTypeForm());
  }

  function startEditRoomType(rt: RoomType) {
    setEditingRoomTypeId(rt.id);
    setRoomTypeForm({
      name: rt.name || '',
      nameZh: rt.nameZh || '',
      nameZhCn: rt.nameZhCn || '',
      description: rt.description || '',
      descriptionZh: rt.descriptionZh || '',
      descriptionZhCn: rt.descriptionZhCn || '',
      pricePerNight: String(rt.pricePerNight ?? ''),
      maxGuests: rt.maxGuests != null ? String(rt.maxGuests) : '',
      inventory: String(rt.inventory ?? 1),
      imageUrl: rt.imageUrl || '',
      amenitiesText: arrayToLines(safeJsonParse<string[]>(rt.amenities, [])),
      bedType: rt.bedType || '',
      view: rt.view || '',
      sizeSqm: rt.sizeSqm != null ? String(rt.sizeSqm) : '',
      occupancy: rt.occupancy || '',
      gallery: safeJsonParse<string[]>(rt.gallery, []),
      featuresText: arrayToLines(safeJsonParse<string[]>(rt.features, [])),
      status: rt.status,
    });
  }

  function closeRoomTypeForm() {
    setEditingRoomTypeId(null);
    setRoomTypeForm(emptyRoomTypeForm());
  }

  async function saveRoomType() {
    if (selectedPropertyId == null || selectedPropertyId === 'new') {
      setError(t('properties.roomTypes.saveFirst'));
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const body = {
        name: roomTypeForm.name.trim() || null,
        nameZh: roomTypeForm.nameZh.trim(),
        nameZhCn: roomTypeForm.nameZhCn.trim() || null,
        description: roomTypeForm.description.trim() || null,
        descriptionZh: roomTypeForm.descriptionZh.trim() || null,
        descriptionZhCn: roomTypeForm.descriptionZhCn.trim() || null,
        pricePerNight: Number(roomTypeForm.pricePerNight) || 0,
        maxGuests: roomTypeForm.maxGuests === '' ? null : Number(roomTypeForm.maxGuests),
        inventory: Number(roomTypeForm.inventory) || 1,
        imageUrl: roomTypeForm.imageUrl.trim() || null,
        amenities: linesToArray(roomTypeForm.amenitiesText),
        bedType: roomTypeForm.bedType.trim() || null,
        view: roomTypeForm.view.trim() || null,
        sizeSqm: roomTypeForm.sizeSqm === '' ? null : Number(roomTypeForm.sizeSqm),
        occupancy: roomTypeForm.occupancy.trim() || null,
        gallery: roomTypeForm.gallery.filter(Boolean),
        features: linesToArray(roomTypeForm.featuresText),
        status: roomTypeForm.status,
      };

      if (!body.nameZh) {
        throw new Error(t('properties.roomTypes.validation'));
      }

      let res: Response;
      if (editingRoomTypeId === 'new') {
        res = await client.api.fetch(`/api/admin/properties/${selectedPropertyId}/room-types`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      } else if (typeof editingRoomTypeId === 'number') {
        res = await client.api.fetch(`/api/admin/room-types/${editingRoomTypeId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      } else {
        throw new Error(t('properties.roomTypes.notSelected'));
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('common.saveFailed'));

      setSuccess(editingRoomTypeId === 'new' ? t('properties.roomTypes.createSuccess') : t('properties.roomTypes.updateSuccess'));
      closeRoomTypeForm();
      await loadPropertyDetail(selectedPropertyId);
    } catch (err: any) {
      setError(err.message || t('properties.roomTypes.saveError'));
    } finally {
      setLoading(false);
    }
  }

  async function confirmDeleteRoomType(id: number) {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await client.api.fetch(`/api/admin/room-types/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('common.deleteFailed'));
      setSuccess(t('properties.roomTypes.deleteSuccess'));
      if (typeof selectedPropertyId === 'number') {
        await loadPropertyDetail(selectedPropertyId);
      }
    } catch (err: any) {
      setError(err.message || t('properties.roomTypes.deleteError'));
    } finally {
      setLoading(false);
      setDeleteConfirmRoomTypeId(null);
    }
  }

  // JSON field helpers for property form
  function addGalleryImage() {
    setPropertyForm({ ...propertyForm, gallery: [...propertyForm.gallery, ''] });
  }

  function updateGalleryImage(index: number, value: string) {
    const next = propertyForm.gallery.map((url, i) => (i === index ? value : url));
    setPropertyForm({ ...propertyForm, gallery: next });
  }

  function removeGalleryImage(index: number) {
    setPropertyForm({ ...propertyForm, gallery: propertyForm.gallery.filter((_, i) => i !== index) });
  }

  function addFacility() {
    setPropertyForm({ ...propertyForm, facilities: [...propertyForm.facilities, { icon: '', label: '' }] });
  }

  function updateFacility(index: number, field: 'icon' | 'label', value: string) {
    const next = propertyForm.facilities.map((f, i) => (i === index ? { ...f, [field]: value } : f));
    setPropertyForm({ ...propertyForm, facilities: next });
  }

  function removeFacility(index: number) {
    setPropertyForm({ ...propertyForm, facilities: propertyForm.facilities.filter((_, i) => i !== index) });
  }

  function toggleExperience(id: number) {
    setSelectedExperienceIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  function toggleRetreat(id: number) {
    setSelectedRetreatIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  function addNearby() {
    setPropertyForm({
      ...propertyForm,
      locationDetails: {
        ...propertyForm.locationDetails,
        nearby: [...propertyForm.locationDetails.nearby, ''],
      },
    });
  }

  function updateNearby(index: number, value: string) {
    const next = propertyForm.locationDetails.nearby.map((n, i) => (i === index ? value : n));
    setPropertyForm({ ...propertyForm, locationDetails: { ...propertyForm.locationDetails, nearby: next } });
  }

  function removeNearby(index: number) {
    setPropertyForm({
      ...propertyForm,
      locationDetails: {
        ...propertyForm.locationDetails,
        nearby: propertyForm.locationDetails.nearby.filter((_, i) => i !== index),
      },
    });
  }

  function updateStory(field: 'title' | 'content', value: string) {
    setPropertyForm({ ...propertyForm, story: { ...propertyForm.story, [field]: value } });
  }

  // Room type gallery helpers
  function addRoomTypeGallery() {
    setRoomTypeForm({ ...roomTypeForm, gallery: [...roomTypeForm.gallery, ''] });
  }

  function updateRoomTypeGallery(index: number, value: string) {
    const next = roomTypeForm.gallery.map((url, i) => (i === index ? value : url));
    setRoomTypeForm({ ...roomTypeForm, gallery: next });
  }

  function removeRoomTypeGallery(index: number) {
    setRoomTypeForm({ ...roomTypeForm, gallery: roomTypeForm.gallery.filter((_, i) => i !== index) });
  }

  function propertyStatusLabel(status: string) {
    switch (status) {
      case 'active':
        return t('common.statusActive');
      case 'inactive':
        return t('common.statusInactive');
      case 'draft':
        return t('common.statusDraft');
      default:
        return status;
    }
  }

  function roomTypeStatusLabel(status: string) {
    switch (status) {
      case 'available':
        return t('common.statusAvailable');
      case 'unavailable':
        return t('common.statusUnavailable');
      case 'hidden':
        return t('common.statusHidden');
      default:
        return status;
    }
  }

  if (selectedPropertyId != null) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <button
            onClick={closePropertyEditor}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            ← {t('properties.backToList')}
          </button>
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 px-4 py-3 rounded-lg">{error}</p>
        )}
        {success && (
          <p className="text-sm text-green-600 bg-green-50 px-4 py-3 rounded-lg">{success}</p>
        )}

        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 relative">
          {loading && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10 rounded-2xl">
              <div className="animate-spin w-8 h-8 border-2 border-[#0a4c6b] border-t-transparent rounded-full" />
            </div>
          )}
          <h3 className="font-bold text-lg text-[#0d1b2a] mb-4">
            {selectedPropertyId === 'new' ? t('properties.addProperty') : t('properties.editProperty')}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('common.nameEn')}</label>
              <input
                type="text"
                value={propertyForm.name}
                onChange={(e) => setPropertyForm({ ...propertyForm, name: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('common.nameZh')}</label>
              <input
                type="text"
                value={propertyForm.nameZh}
                onChange={(e) => setPropertyForm({ ...propertyForm, nameZh: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('common.nameZhCn')}</label>
              <input
                type="text"
                value={propertyForm.nameZhCn}
                onChange={(e) => setPropertyForm({ ...propertyForm, nameZhCn: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('common.location')}</label>
              <input
                type="text"
                value={propertyForm.location}
                onChange={(e) => setPropertyForm({ ...propertyForm, location: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('common.status')}</label>
              <select
                value={propertyForm.status}
                onChange={(e) =>
                  setPropertyForm({ ...propertyForm, status: e.target.value as 'active' | 'inactive' | 'draft' })
                }
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              >
                <option value="active">{t('common.statusActive')}</option>
                <option value="inactive">{t('common.statusInactive')}</option>
                <option value="draft">{t('common.statusDraft')}</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('properties.pricePerNight')}</label>
              <input
                type="number"
                value={propertyForm.pricePerNight}
                onChange={(e) => setPropertyForm({ ...propertyForm, pricePerNight: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('properties.maxGuests')}</label>
              <input
                type="number"
                value={propertyForm.maxGuests}
                onChange={(e) => setPropertyForm({ ...propertyForm, maxGuests: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">{t('common.mainImageUrl')}</label>
              <input
                type="text"
                value={propertyForm.imageUrl}
                onChange={(e) => setPropertyForm({ ...propertyForm, imageUrl: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
              <ImagePreview url={propertyForm.imageUrl} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">{t('common.descriptionEn')}</label>
              <textarea
                value={propertyForm.description}
                onChange={(e) => setPropertyForm({ ...propertyForm, description: e.target.value })}
                rows={3}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">{t('common.descriptionZh')}</label>
              <textarea
                value={propertyForm.descriptionZh}
                onChange={(e) => setPropertyForm({ ...propertyForm, descriptionZh: e.target.value })}
                rows={3}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">{t('common.descriptionZhCn')}</label>
              <textarea
                value={propertyForm.descriptionZhCn}
                onChange={(e) => setPropertyForm({ ...propertyForm, descriptionZhCn: e.target.value })}
                rows={3}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
            </div>
          </div>

          {/* Gallery */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-[#0d1b2a]">{t('properties.gallery')}</label>
              <button
                onClick={addGalleryImage}
                className="text-xs bg-[#B8902F]/10 text-[#B8902F] px-3 py-1 rounded-lg hover:bg-[#B8902F]/20 transition"
              >
                + {t('properties.addImage')}
              </button>
            </div>
            <div className="space-y-2">
              {propertyForm.gallery.length === 0 && (
                <p className="text-sm text-gray-400">{t('properties.noImages')}</p>
              )}
              {propertyForm.gallery.map((url, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <ImagePreview url={url} />
                  <input
                    type="text"
                    value={url}
                    onChange={(e) => updateGalleryImage(idx, e.target.value)}
                    placeholder={t('properties.imageUrlPlaceholder')}
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                  />
                  <button
                    onClick={() => removeGalleryImage(idx)}
                    className="text-xs text-red-500 hover:text-red-700 px-2 py-2"
                  >
                    {t('common.delete')}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Facilities */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-[#0d1b2a]">{t('properties.facilities')}</label>
              <button
                onClick={addFacility}
                className="text-xs bg-[#B8902F]/10 text-[#B8902F] px-3 py-1 rounded-lg hover:bg-[#B8902F]/20 transition"
              >
                + {t('properties.addFacility')}
              </button>
            </div>
            <div className="space-y-2">
              {propertyForm.facilities.length === 0 && (
                <p className="text-sm text-gray-400">{t('properties.noFacilities')}</p>
              )}
              {propertyForm.facilities.map((f, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={f.icon}
                    onChange={(e) => updateFacility(idx, 'icon', e.target.value)}
                    placeholder={t('properties.facilityIconPlaceholder')}
                    className="w-24 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                  />
                  <input
                    type="text"
                    value={f.label}
                    onChange={(e) => updateFacility(idx, 'label', e.target.value)}
                    placeholder={t('properties.facilityNamePlaceholder')}
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                  />
                  <button
                    onClick={() => removeFacility(idx)}
                    className="text-xs text-red-500 hover:text-red-700 px-2 py-2"
                  >
                    {t('common.delete')}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Linked Experiences & Retreats */}
          <div className="mb-6">
            <label className="text-sm font-medium text-[#0d1b2a] mb-2 block">{t('properties.linkedExperiences')}</label>
            <p className="text-xs text-gray-500 mb-3">{t('properties.linkedExperiencesHint')}</p>

            <div className="mb-4">
              <p className="text-xs font-medium text-gray-600 mb-2">{t('properties.experiences')}</p>
              {allExperiences.length === 0 ? (
                <p className="text-sm text-gray-400">{t('properties.noExperiences')}</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {allExperiences.map((e) => {
                    const checked = selectedExperienceIds.includes(e.id);
                    return (
                      <label
                        key={e.id}
                        className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${
                          checked ? 'border-[#0a4c6b] bg-[#0a4c6b]/5' : 'border-gray-200 hover:border-[#2ec4b6]'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleExperience(e.id)}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <p className="font-medium text-sm text-[#0d1b2a]">{e.nameZh}</p>
                          <p className="text-xs text-gray-500">{e.name} · {e.duration}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <p className="text-xs font-medium text-gray-600 mb-2">{t('properties.retreats')}</p>
              {allRetreats.length === 0 ? (
                <p className="text-sm text-gray-400">{t('properties.noRetreats')}</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {allRetreats.map((r) => {
                    const checked = selectedRetreatIds.includes(r.id);
                    return (
                      <label
                        key={r.id}
                        className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${
                          checked ? 'border-[#0a4c6b] bg-[#0a4c6b]/5' : 'border-gray-200 hover:border-[#2ec4b6]'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleRetreat(r.id)}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <p className="font-medium text-sm text-[#0d1b2a]">{r.nameZh}</p>
                          <p className="text-xs text-gray-500">{r.name} · {r.duration}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Location details -->
          <div className="mb-6">
            <label className="text-sm font-medium text-[#0d1b2a] mb-2 block">{t('properties.locationDetails')}</label>
            <div className="space-y-3 border rounded-lg p-4 bg-gray-50">
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t('properties.locationDescription')}</label>
                <textarea
                  value={propertyForm.locationDetails.description}
                  onChange={(e) =>
                    setPropertyForm({
                      ...propertyForm,
                      locationDetails: { ...propertyForm.locationDetails, description: e.target.value },
                    })
                  }
                  rows={3}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t('properties.mapImageUrl')}</label>
                <input
                  type="text"
                  value={propertyForm.locationDetails.mapImage}
                  onChange={(e) =>
                    setPropertyForm({
                      ...propertyForm,
                      locationDetails: { ...propertyForm.locationDetails, mapImage: e.target.value },
                    })
                  }
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                />
                <ImagePreview url={propertyForm.locationDetails.mapImage} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs text-gray-500">{t('properties.nearby')}</label>
                  <button
                    onClick={addNearby}
                    className="text-xs bg-[#B8902F]/10 text-[#B8902F] px-3 py-1 rounded-lg hover:bg-[#B8902F]/20 transition"
                  >
                    + {t('common.add')}
                  </button>
                </div>
                <div className="space-y-2">
                  {propertyForm.locationDetails.nearby.length === 0 && (
                    <p className="text-sm text-gray-400">{t('properties.noNearby')}</p>
                  )}
                  {propertyForm.locationDetails.nearby.map((n, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={n}
                        onChange={(e) => updateNearby(idx, e.target.value)}
                        placeholder={t('properties.nearby')}
                        className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                      />
                      <button
                        onClick={() => removeNearby(idx)}
                        className="text-xs text-red-500 hover:text-red-700 px-2 py-2"
                      >
                        {t('common.delete')}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Story */}
          <div className="mb-6">
            <label className="text-sm font-medium text-[#0d1b2a] mb-2 block">{t('properties.story')}</label>
            <div className="space-y-3 border rounded-lg p-4 bg-gray-50">
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t('properties.storyTitle')}</label>
                <input
                  type="text"
                  value={propertyForm.story.title}
                  onChange={(e) => updateStory('title', e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t('properties.storyContent')}</label>
                <textarea
                  value={propertyForm.story.content}
                  onChange={(e) => updateStory('content', e.target.value)}
                  rows={4}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={saveProperty}
              disabled={loading}
              className="bg-[#0a4c6b] text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-[#083d56] transition disabled:opacity-60"
            >
              {loading ? t('common.saving') : t('properties.saveProperty')}
            </button>
            <button
              onClick={closePropertyEditor}
              className="text-sm text-gray-500 hover:text-gray-700 px-3 py-2"
            >
              {t('common.cancel')}
            </button>
            {selectedPropertyId !== 'new' && (
              <>
                {deleteConfirmPropertyId === selectedPropertyId ? (
                  <div className="flex items-center gap-2 ml-auto">
                    <button
                      onClick={() => confirmDeleteProperty(selectedPropertyId)}
                      className="text-xs bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600"
                    >
                      {t('properties.confirmDeleteProperty')}
                    </button>
                    <button
                      onClick={() => setDeleteConfirmPropertyId(null)}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setDeleteConfirmPropertyId(selectedPropertyId)}
                    className="text-xs text-red-500 hover:text-red-700 ml-auto px-3 py-2 rounded-lg hover:bg-red-50 transition"
                  >
                    {t('properties.deleteProperty')}
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Room types editor */}
        {selectedPropertyId !== 'new' && (
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg text-[#0d1b2a]">{t('properties.roomTypes.title')}</h3>
              <button
                onClick={startNewRoomType}
                className="bg-[#B8902F] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#a07f29] transition"
              >
                {t('properties.roomTypes.addRoomType')}
              </button>
            </div>

            {editingRoomTypeId != null && (
              <div className="border rounded-xl p-4 mb-6 bg-gray-50">
                <h4 className="font-semibold text-[#0d1b2a] mb-3">
                  {editingRoomTypeId === 'new' ? t('properties.roomTypes.addRoomType') : t('properties.roomTypes.editTitle')}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('common.nameEn')}</label>
                    <input
                      type="text"
                      value={roomTypeForm.name}
                      onChange={(e) => setRoomTypeForm({ ...roomTypeForm, name: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('common.nameZh')}</label>
                    <input
                      type="text"
                      value={roomTypeForm.nameZh}
                      onChange={(e) => setRoomTypeForm({ ...roomTypeForm, nameZh: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('common.nameZhCn')}</label>
                    <input
                      type="text"
                      value={roomTypeForm.nameZhCn}
                      onChange={(e) => setRoomTypeForm({ ...roomTypeForm, nameZhCn: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('common.status')}</label>
                    <select
                      value={roomTypeForm.status}
                      onChange={(e) =>
                        setRoomTypeForm({
                          ...roomTypeForm,
                          status: e.target.value as 'available' | 'unavailable' | 'hidden',
                        })
                      }
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                    >
                      <option value="available">{t('common.statusAvailable')}</option>
                      <option value="unavailable">{t('common.statusUnavailable')}</option>
                      <option value="hidden">{t('common.statusHidden')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('properties.roomTypes.pricePerNight')}</label>
                    <input
                      type="number"
                      value={roomTypeForm.pricePerNight}
                      onChange={(e) => setRoomTypeForm({ ...roomTypeForm, pricePerNight: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('properties.maxGuests')}</label>
                    <input
                      type="number"
                      value={roomTypeForm.maxGuests}
                      onChange={(e) => setRoomTypeForm({ ...roomTypeForm, maxGuests: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('properties.roomTypes.inventory')}</label>
                    <input
                      type="number"
                      value={roomTypeForm.inventory}
                      onChange={(e) => setRoomTypeForm({ ...roomTypeForm, inventory: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('properties.roomTypes.bedType')}</label>
                    <input
                      type="text"
                      value={roomTypeForm.bedType}
                      onChange={(e) => setRoomTypeForm({ ...roomTypeForm, bedType: e.target.value })}
                      placeholder={t('properties.roomTypes.bedTypePlaceholder')}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('properties.roomTypes.view')}</label>
                    <input
                      type="text"
                      value={roomTypeForm.view}
                      onChange={(e) => setRoomTypeForm({ ...roomTypeForm, view: e.target.value })}
                      placeholder={t('properties.roomTypes.viewPlaceholder')}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('properties.roomTypes.sizeSqm')}</label>
                    <input
                      type="number"
                      value={roomTypeForm.sizeSqm}
                      onChange={(e) => setRoomTypeForm({ ...roomTypeForm, sizeSqm: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('properties.roomTypes.occupancy')}</label>
                    <input
                      type="text"
                      value={roomTypeForm.occupancy}
                      onChange={(e) => setRoomTypeForm({ ...roomTypeForm, occupancy: e.target.value })}
                      placeholder={t('properties.roomTypes.occupancyPlaceholder')}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">{t('common.mainImageUrl')}</label>
                    <input
                      type="text"
                      value={roomTypeForm.imageUrl}
                      onChange={(e) => setRoomTypeForm({ ...roomTypeForm, imageUrl: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                    />
                    <ImagePreview url={roomTypeForm.imageUrl} />
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-xs text-gray-500 mb-1">{t('common.descriptionEn')}</label>
                    <textarea
                      value={roomTypeForm.description}
                      onChange={(e) => setRoomTypeForm({ ...roomTypeForm, description: e.target.value })}
                      rows={2}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-xs text-gray-500 mb-1">{t('common.descriptionZh')}</label>
                    <textarea
                      value={roomTypeForm.descriptionZh}
                      onChange={(e) => setRoomTypeForm({ ...roomTypeForm, descriptionZh: e.target.value })}
                      rows={2}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-xs text-gray-500 mb-1">{t('common.descriptionZhCn')}</label>
                    <textarea
                      value={roomTypeForm.descriptionZhCn}
                      onChange={(e) => setRoomTypeForm({ ...roomTypeForm, descriptionZhCn: e.target.value })}
                      rows={2}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-xs text-gray-500 mb-1">{t('properties.roomTypes.amenities')}</label>
                    <textarea
                      value={roomTypeForm.amenitiesText}
                      onChange={(e) => setRoomTypeForm({ ...roomTypeForm, amenitiesText: e.target.value })}
                      rows={3}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-xs text-gray-500 mb-1">{t('properties.roomTypes.features')}</label>
                    <textarea
                      value={roomTypeForm.featuresText}
                      onChange={(e) => setRoomTypeForm({ ...roomTypeForm, featuresText: e.target.value })}
                      rows={3}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs text-gray-500">{t('properties.roomTypes.gallery')}</label>
                    <button
                      onClick={addRoomTypeGallery}
                      className="text-xs bg-[#B8902F]/10 text-[#B8902F] px-3 py-1 rounded-lg hover:bg-[#B8902F]/20 transition"
                    >
                      + {t('properties.addImage')}
                    </button>
                  </div>
                  <div className="space-y-2">
                    {roomTypeForm.gallery.length === 0 && (
                      <p className="text-sm text-gray-400">{t('properties.noImages')}</p>
                    )}
                    {roomTypeForm.gallery.map((url, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <ImagePreview url={url} />
                        <input
                          type="text"
                          value={url}
                          onChange={(e) => updateRoomTypeGallery(idx, e.target.value)}
                          placeholder={t('properties.imageUrlPlaceholder')}
                          className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                        />
                        <button
                          onClick={() => removeRoomTypeGallery(idx)}
                          className="text-xs text-red-500 hover:text-red-700 px-2 py-2"
                        >
                          {t('common.delete')}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={saveRoomType}
                    disabled={loading}
                    className="bg-[#0a4c6b] text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-[#083d56] transition disabled:opacity-60"
                  >
                    {loading ? t('common.saving') : editingRoomTypeId === 'new' ? t('properties.roomTypes.addRoomType') : t('properties.roomTypes.updateRoomType')}
                  </button>
                  <button
                    onClick={closeRoomTypeForm}
                    className="text-sm text-gray-500 hover:text-gray-700 px-3 py-2"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">{t('common.nameZh')}</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">{t('properties.roomTypes.pricePerNight')}</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">{t('properties.roomTypes.columns.inventory')}</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">{t('common.status')}</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {roomTypes.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                        {t('properties.roomTypes.empty')}
                      </td>
                    </tr>
                  ) : (
                    roomTypes.map((rt) => (
                      <tr key={rt.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-[#0d1b2a]">{rt.nameZh}</td>
                        <td className="px-4 py-3 text-gray-600">
                          HK${Number(rt.pricePerNight).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{rt.inventory}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(rt.status)}`}>
                            {roomTypeStatusLabel(rt.status)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => startEditRoomType(rt)}
                              className="text-xs text-[#0a4c6b] hover:underline"
                            >
                              {t('common.edit')}
                            </button>
                            {deleteConfirmRoomTypeId === rt.id ? (
                              <>
                                <button
                                  onClick={() => confirmDeleteRoomType(rt.id)}
                                  className="text-xs bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                                >
                                  {t('common.confirm')}
                                </button>
                                <button
                                  onClick={() => setDeleteConfirmRoomTypeId(null)}
                                  className="text-xs text-gray-500 hover:text-gray-700"
                                >
                                  {t('common.cancel')}
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => setDeleteConfirmRoomTypeId(rt.id)}
                                className="text-xs text-red-500 hover:text-red-700"
                              >
                                {t('common.delete')}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg text-[#0d1b2a]">{t('tabs.properties')}</h3>
        <button
          onClick={openNewProperty}
          className="bg-[#0a4c6b] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#083d56] transition"
        >
          {t('properties.addProperty')}
        </button>
      </div>

      {error && (
        <p className="text-sm text-red-600 bg-red-50 px-4 py-3 rounded-lg">{error}</p>
      )}
      {success && (
        <p className="text-sm text-green-600 bg-green-50 px-4 py-3 rounded-lg">{success}</p>
      )}

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">{t('common.nameZh')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">{t('common.location')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">{t('common.status')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {properties.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-center text-gray-500">
                    {t('properties.empty')}
                  </td>
                </tr>
              ) : (
                properties.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-[#0d1b2a]">{p.nameZh}</td>
                    <td className="px-4 py-3 text-gray-600">{p.location || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(p.status)}`}>
                        {propertyStatusLabel(p.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedPropertyId(p.id);
                            setError('');
                            setSuccess('');
                            setDeleteConfirmPropertyId(null);
                          }}
                          className="text-xs text-[#0a4c6b] hover:underline"
                        >
                          {t('properties.editRoomTypeAction')}
                        </button>
                        {deleteConfirmPropertyId === p.id ? (
                          <>
                            <button
                              onClick={() => confirmDeleteProperty(p.id)}
                              className="text-xs bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                            >
                              {t('common.confirm')}
                            </button>
                            <button
                              onClick={() => setDeleteConfirmPropertyId(null)}
                              className="text-xs text-gray-500 hover:text-gray-700"
                            >
                              {t('common.cancel')}
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirmPropertyId(p.id)}
                            className="text-xs text-red-500 hover:text-red-700"
                          >
                            {t('common.delete')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Referral Section
// ============================================================================

interface ReferrerItem {
  id: number;
  name: string;
  phone: string | null;
  referralCode: string;
  token: string;
  status: 'active' | 'inactive';
  totalReferrals: number;
  totalCommission: number;
  paidCommission: number;
  referralLink: string;
  dashboardLink: string;
  whatsappDeeplink: string;
  qrCodeUrl: string;
}

interface ReferralOrderItem {
  id: number;
  booking_id: number;
  booking_token: string | null;
  referrer_id: number;
  referrer_name: string;
  order_amount: number;
  commission_amount: number;
  currency: string;
  status: 'pending' | 'approved' | 'paid' | 'cancelled';
  created_at: number;
}

interface ReferralRules {
  mode: 'percentage' | 'fixed';
  percentage: number;
  fixed_amount: number;
  currency: string;
}

function ReferralSection() {
  const { t } = useTranslation('admin');
  const [subTab, setSubTab] = useState<'partners' | 'orders' | 'settings'>('partners');
  const [referrers, setReferrers] = useState<ReferrerItem[]>([]);
  const [orders, setOrders] = useState<ReferralOrderItem[]>([]);
  const [rules, setRules] = useState<ReferralRules>({ mode: 'percentage', percentage: 5, fixed_amount: 0, currency: 'HKD' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  useEffect(() => {
    if (subTab === 'partners') loadReferrers();
    else if (subTab === 'orders') loadOrders();
    else if (subTab === 'settings') loadSettings();
  }, [subTab]);

  async function loadReferrers() {
    setLoading(true);
    setError('');
    try {
      const res = await client.api.fetch('/api/admin/referral/referrals');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('common.loadFailed'));
      setReferrers(data.data || []);
    } catch (err: any) {
      setError(err.message || t('referral.loadPartnersError'));
    } finally {
      setLoading(false);
    }
  }

  async function loadOrders() {
    setLoading(true);
    setError('');
    try {
      const res = await client.api.fetch('/api/admin/referral/referral-orders');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('common.loadFailed'));
      setOrders(data.data || []);
    } catch (err: any) {
      setError(err.message || t('referral.loadOrdersError'));
    } finally {
      setLoading(false);
    }
  }

  async function loadSettings() {
    setLoading(true);
    setError('');
    try {
      const res = await client.api.fetch('/api/admin/referral/referral-settings');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('common.loadFailed'));
      setRules(data.data || { mode: 'percentage', percentage: 5, fixed_amount: 0, currency: 'HKD' });
    } catch (err: any) {
      setError(err.message || t('referral.loadSettingsError'));
    } finally {
      setLoading(false);
    }
  }

  async function createReferrer(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!newName.trim()) {
      setError(t('referral.validationName'));
      return;
    }
    try {
      const res = await client.api.fetch('/api/admin/referral/referrals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim(), phone: newPhone.trim() || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('referral.createFailed'));
      setNewName('');
      setNewPhone('');
      setSuccess(t('referral.createSuccess'));
      await loadReferrers();
    } catch (err: any) {
      setError(err.message || t('referral.createFailed'));
    }
  }

  async function toggleStatus(id: number, current: 'active' | 'inactive') {
    const next = current === 'active' ? 'inactive' : 'active';
    try {
      const res = await client.api.fetch(`/api/admin/referral/referrals/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: next }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || t('common.updateFailed'));
      }
      await loadReferrers();
    } catch (err: any) {
      setError(err.message || t('common.updateFailed'));
    }
  }

  async function resendWelcome(id: number) {
    setError('');
    setSuccess('');
    try {
      const res = await client.api.fetch(`/api/admin/referral/referrals/${id}/resend`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('common.sendFailed'));
      setSuccess(t('referral.resendSuccess'));
    } catch (err: any) {
      setError(err.message || t('common.sendFailed'));
    }
  }

  async function updateOrderStatus(id: number, status: ReferralOrderItem['status']) {
    try {
      const res = await client.api.fetch(`/api/admin/referral/referral-orders/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || t('common.updateFailed'));
      }
      await loadOrders();
    } catch (err: any) {
      setError(err.message || t('common.updateFailed'));
    }
  }

  async function saveSettings(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      const res = await client.api.fetch('/api/admin/referral/referral-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(rules),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('common.saveFailed'));
      setRules(data.data);
      setSuccess(t('referral.settingsSuccess'));
    } catch (err: any) {
      setError(err.message || t('common.saveFailed'));
    }
  }

  function copy(text: string, key: string) {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    });
  }

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-blue-100 text-blue-700',
      paid: 'bg-green-100 text-green-700',
      cancelled: 'bg-gray-100 text-gray-600',
      active: 'bg-green-100 text-green-700',
      inactive: 'bg-gray-100 text-gray-600',
    };
    const label: Record<string, string> = {
      pending: t('referral.status.pending'), approved: t('referral.status.approved'), paid: t('referral.status.paid'), cancelled: t('referral.status.cancelled'),
      active: t('referral.enable'), inactive: t('referral.disable'),
    };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-gray-100'}`}>{label[status] || status}</span>;
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h2 className="text-xl font-bold text-[#0d1b2a] mb-4">{t('referral.title')}</h2>
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'partners', label: t('referral.tabPartners') },
            { key: 'orders', label: t('referral.tabOrders') },
            { key: 'settings', label: t('referral.tabSettings') },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setSubTab(t.key as typeof subTab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                subTab === t.key
                  ? 'bg-[#0a4c6b] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-700 rounded-xl px-4 py-3 text-sm">{error}</div>}
      {success && <div className="bg-green-50 text-green-700 rounded-xl px-4 py-3 text-sm">{success}</div>}

      {subTab === 'partners' && (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h3 className="font-bold text-[#0d1b2a] mb-4">{t('referral.addPartner')}</h3>
            <form onSubmit={createReferrer} className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={t('referral.partnerNamePlaceholder')}
                className="flex-1 border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
              <input
                type="tel"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                placeholder={t('referral.partnerPhonePlaceholder')}
                className="flex-1 border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
              <button
                type="submit"
                disabled={loading}
                className="bg-[#0a4c6b] text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-[#083d56] transition disabled:opacity-60"
              >
                {t('referral.create')}
              </button>
            </form>
          </div>

          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h3 className="font-bold text-[#0d1b2a]">{t('referral.partnerList')}</h3>
            </div>
            {loading ? (
              <div className="p-8 text-center text-gray-500">{t('common.loading')}</div>
            ) : referrers.length === 0 ? (
              <div className="p-8 text-center text-gray-400">{t('referral.emptyPartners')}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">{t('common.name')}</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Code</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">{t('common.status')}</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">{t('referral.ordersColumns.dealCommission')}</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">{t('common.link')}</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {referrers.map((r) => (
                      <tr key={r.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-[#0d1b2a]">{r.name}</p>
                          {r.phone && <p className="text-xs text-gray-500">{r.phone}</p>}
                        </td>
                        <td className="px-4 py-3 font-mono text-[#0a4c6b]">{r.referralCode}</td>
                        <td className="px-4 py-3">{statusBadge(r.status)}</td>
                        <td className="px-4 py-3">
                          <p>{t('referral.dealCount', { count: r.totalReferrals })}</p>
                          <p className="text-xs text-gray-500">HK${r.totalCommission.toLocaleString()} / HK${r.paidCommission.toLocaleString()}</p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => copy(r.referralLink, `link-${r.id}`)}
                              className="text-xs bg-[#f0f9f7] text-[#0a4c6b] px-2 py-1 rounded hover:bg-[#e0f3ef] transition"
                            >
                              {copiedKey === `link-${r.id}` ? t('common.copied') : t('referral.shareLink')}
                            </button>
                            <button
                              onClick={() => copy(r.dashboardLink, `dash-${r.id}`)}
                              className="text-xs bg-[#f0f9f7] text-[#0a4c6b] px-2 py-1 rounded hover:bg-[#e0f3ef] transition"
                            >
                              {copiedKey === `dash-${r.id}` ? t('common.copied') : t('referral.dashboardLink')}
                            </button>
                            <button
                              onClick={() => copy(r.whatsappDeeplink, `wa-${r.id}`)}
                              className="text-xs bg-[#f0f9f7] text-[#0a4c6b] px-2 py-1 rounded hover:bg-[#e0f3ef] transition"
                            >
                              {copiedKey === `wa-${r.id}` ? t('common.copied') : t('referral.whatsapp')}
                            </button>
                            <a
                              href={r.qrCodeUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs bg-[#f8f5ed] text-[#B8902F] px-2 py-1 rounded hover:bg-[#f0e9db] transition"
                            >
                              {t('referral.qrCode')}
                            </a>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => toggleStatus(r.id, r.status)}
                              className="text-xs px-2 py-1 rounded border border-gray-200 hover:bg-gray-100 transition"
                            >
                              {r.status === 'active' ? t('referral.disable') : t('referral.enable')}
                            </button>
                            {r.phone && (
                              <button
                                onClick={() => resendWelcome(r.id)}
                                className="text-xs px-2 py-1 rounded border border-gray-200 hover:bg-gray-100 transition"
                              >
                                {t('referral.resendMessage')}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {subTab === 'orders' && (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h3 className="font-bold text-[#0d1b2a]">{t('referral.tabOrders')}</h3>
          </div>
          {loading ? (
            <div className="p-8 text-center text-gray-500">{t('common.loading')}</div>
          ) : orders.length === 0 ? (
            <div className="p-8 text-center text-gray-400">{t('referral.emptyOrders')}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">{t('referral.ordersColumns.booking')}</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">{t('referral.ordersColumns.partner')}</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">{t('referral.ordersColumns.amount')}</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">{t('referral.ordersColumns.commission')}</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">{t('common.status')}</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">{t('common.update')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {orders.map((o) => (
                    <tr key={o.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono">#{o.booking_token || o.booking_id}</td>
                      <td className="px-4 py-3">{o.referrer_name}</td>
                      <td className="px-4 py-3">HK${o.order_amount.toLocaleString()}</td>
                      <td className="px-4 py-3 font-medium text-[#0a4c6b]">HK${o.commission_amount.toLocaleString()}</td>
                      <td className="px-4 py-3">{statusBadge(o.status)}</td>
                      <td className="px-4 py-3">
                        <select
                          value={o.status}
                          onChange={(e) => updateOrderStatus(o.id, e.target.value as ReferralOrderItem['status'])}
                          className="border border-gray-200 rounded-lg px-2 py-1 text-xs focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                        >
                          <option value="pending">{t('referral.status.pending')}</option>
                          <option value="approved">{t('referral.status.approved')}</option>
                          <option value="paid">{t('referral.status.paid')}</option>
                          <option value="cancelled">{t('referral.status.cancelled')}</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {subTab === 'settings' && (
        <div className="bg-white rounded-2xl p-6 shadow-sm max-w-xl">
          <h3 className="font-bold text-[#0d1b2a] mb-4">{t('referral.tabSettings')}</h3>
          <form onSubmit={saveSettings} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('referral.settings.mode')}</label>
              <select
                value={rules.mode}
                onChange={(e) => setRules({ ...rules, mode: e.target.value as 'percentage' | 'fixed' })}
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              >
                <option value="percentage">{t('referral.settings.percentage')}</option>
                <option value="fixed">{t('referral.settings.fixed')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {rules.mode === 'percentage' ? t('referral.settings.percentage') : t('referral.settings.fixedAmount')}
              </label>
              <input
                type="number"
                min={0}
                step={rules.mode === 'percentage' ? 0.1 : 1}
                value={rules.mode === 'percentage' ? rules.percentage : rules.fixed_amount}
                onChange={(e) =>
                  setRules({
                    ...rules,
                    [rules.mode === 'percentage' ? 'percentage' : 'fixed_amount']: Number(e.target.value),
                  })
                }
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('referral.settings.currency')}</label>
              <input
                type="text"
                value={rules.currency}
                onChange={(e) => setRules({ ...rules, currency: e.target.value.toUpperCase() })}
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="bg-[#0a4c6b] text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-[#083d56] transition disabled:opacity-60"
            >
              {t('referral.settings.save')}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Admin Page
// ============================================================================

export default function AdminPage() {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const { user, isAdmin, isChecking, adminRole } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'dashboard' | 'bookings' | 'properties' | 'experiences' | 'packages' | 'customers' | 'leads' | 'payments' | 'referral' | 'whatsapp' | 'accounts' | 'translations'>('dashboard');

  // Guard: redirect to login if not authenticated as admin
  useEffect(() => {
    if (!isChecking && (!user || !isAdmin)) {
      navigate('/auth');
    }
  }, [isChecking, user, isAdmin, navigate]);
  const [bookings, setBookings] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<AdminAccount[]>([]);
  const [loading, setLoading] = useState(true);

  // Booking management state
  const [selectedBooking, setSelectedBooking] = useState<any | null>(null);
  const [bookingStatusFilter, setBookingStatusFilter] = useState<string>('');
  const [bookingPaymentFilter, setBookingPaymentFilter] = useState<string>('');
  const [bookingSearch, setBookingSearch] = useState('');
  const [bookingDateFrom, setBookingDateFrom] = useState('');
  const [bookingDateTo, setBookingDateTo] = useState('');
  const [bookingPage, setBookingPage] = useState(1);
  const [bookingTotal, setBookingTotal] = useState(0);
  const [cancelReason, setCancelReason] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('payme');
  const [paymentReference, setPaymentReference] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [bookingActionLoading, setBookingActionLoading] = useState(false);
  const [editingBooking, setEditingBooking] = useState(false);
  const [editBookingForm, setEditBookingForm] = useState<{ checkIn: string; checkOut: string; roomTypeId: number; guests: number; totalAmount: number } | null>(null);

  const [bookingLinkedExperiences, setBookingLinkedExperiences] = useState<Experience[]>([]);
  const [bookingLinkedRetreats, setBookingLinkedRetreats] = useState<Retreat[]>([]);
  const [bookingAddonsDraft, setBookingAddonsDraft] = useState<Array<{ type: 'experience' | 'retreat'; id: number; name: string; nameZh: string }>>([]);
  const bookingLimit = 20;

  // Account management state
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<'admin' | 'superadmin'>('admin');
  const [accountError, setAccountError] = useState('');
  const [accountSuccess, setAccountSuccess] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    if (activeTab === 'bookings') fetchBookings();
    else if (activeTab === 'accounts') fetchAccounts();
    else setLoading(false);
  }, [activeTab, bookingStatusFilter, bookingPaymentFilter, bookingSearch, bookingDateFrom, bookingDateTo, bookingPage]);

  async function fetchBookings() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', String(bookingLimit));
      params.append('offset', String((bookingPage - 1) * bookingLimit));
      if (bookingStatusFilter) params.append('status', bookingStatusFilter);
      if (bookingPaymentFilter) params.append('payment_status', bookingPaymentFilter);
      if (bookingSearch.trim()) params.append('search', bookingSearch.trim());
      if (bookingDateFrom) params.append('check_in_from', String(new Date(bookingDateFrom).getTime() / 1000));
      if (bookingDateTo) params.append('check_in_to', String(new Date(bookingDateTo).getTime() / 1000 + 24 * 60 * 60 - 1));
      const res = await client.api.fetch(`/api/admin/bookings?${params.toString()}`);
      const data = await res.json();
      setBookings(data.data || []);
      setBookingTotal(data.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  async function fetchAccounts() {
    setLoading(true);
    try {
      const res = await client.api.fetch('/api/admin/accounts');
      const data = await res.json();
      setAccounts(data.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  async function confirmBooking(id: number) {
    setBookingActionLoading(true);
    try {
      await client.api.fetch(`/api/admin/bookings/${id}/confirm`, { method: 'PATCH' });
      await fetchBookings();
      await refreshSelectedBooking(id);
    } catch (err) { console.error(err); }
    finally { setBookingActionLoading(false); }
  }

  async function markBookingPaid(id: number) {
    setBookingActionLoading(true);
    try {
      await client.api.fetch(`/api/admin/bookings/${id}/mark-paid`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentMethod, paymentReference }),
      });
      setPaymentReference('');
      await fetchBookings();
      await refreshSelectedBooking(id);
    } catch (err) { console.error(err); }
    finally { setBookingActionLoading(false); }
  }

  async function cancelBooking(id: number) {
    setBookingActionLoading(true);
    try {
      const res = await client.api.fetch(`/api/admin/bookings/${id}/cancel`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: cancelReason }),
      });
      const data = await res.json();
      setCancelReason('');
      await fetchBookings();
      await refreshSelectedBooking(id);
      alert(t('bookings.cancelAlert', { amount: data.refund?.amount?.toLocaleString() ?? 0, percent: data.refund?.percent ?? 0 }));
    } catch (err) { console.error(err); }
    finally { setBookingActionLoading(false); }
  }

  async function saveBookingNotes(id: number) {
    setBookingActionLoading(true);
    try {
      await client.api.fetch(`/api/admin/bookings/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminNotes }),
      });
      await fetchBookings();
      await refreshSelectedBooking(id);
    } catch (err) { console.error(err); }
    finally { setBookingActionLoading(false); }
  }

  async function refreshSelectedBooking(id: number) {
    if (selectedBooking?.id !== id) return;
    try {
      const res = await client.api.fetch(`/api/admin/bookings/${id}`);
      const data = await res.json();
      if (data.data) setSelectedBooking(data.data);
    } catch (err) { console.error(err); }
  }

  function parseBookingAddons(addons: any) {
    try {
      const arr = JSON.parse(addons || '[]');
      if (!Array.isArray(arr)) return [];
      return arr
        .filter((a: any) => a && (a.type === 'experience' || a.type === 'retreat') && a.id)
        .map((a: any) => ({
          type: a.type as 'experience' | 'retreat',
          id: Number(a.id),
          name: String(a.name || ''),
          nameZh: String(a.nameZh || ''),
        }));
    } catch { return []; }
  }

  async function loadBookingLinkedItems(propertyId: number) {
    try {
      const res = await client.api.fetch(`/api/admin/properties/${propertyId}`);
      const data = await res.json();
      if (data.data) {
        setBookingLinkedExperiences(data.data.experiences || []);
        setBookingLinkedRetreats(data.data.retreats || []);
      }
    } catch (err) { console.error(err); }
  }

  function toggleBookingAddon(item: Experience | Retreat, type: 'experience' | 'retreat') {
    setBookingAddonsDraft((prev) => {
      const exists = prev.some((a) => a.id === item.id && a.type === type);
      if (exists) return prev.filter((a) => !(a.id === item.id && a.type === type));
      return [...prev, { type, id: item.id, name: item.name, nameZh: item.nameZh }];
    });
  }

  async function saveBookingAddons(id: number) {
    setBookingActionLoading(true);
    try {
      const res = await client.api.fetch(`/api/admin/bookings/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ addons: bookingAddonsDraft }),
      });
      if (!res.ok) throw new Error(t('common.updateFailed'));
      await fetchBookings();
      await refreshSelectedBooking(id);
    } catch (err) { console.error(err); }
    finally { setBookingActionLoading(false); }
  }

  useEffect(() => {
    if (selectedBooking?.propertyId) {
      loadBookingLinkedItems(selectedBooking.propertyId);
    } else {
      setBookingLinkedExperiences([]);
      setBookingLinkedRetreats([]);
    }
    setBookingAddonsDraft(parseBookingAddons(selectedBooking?.addons));
  }, [selectedBooking?.id]);

  async function updateSupplierStatus(id: number, supplierStatus: string) {
    setBookingActionLoading(true);
    try {
      await client.api.fetch(`/api/admin/bookings/${id}/supplier-status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ supplierStatus }),
      });
      await fetchBookings();
      await refreshSelectedBooking(id);
    } catch (err) { console.error(err); }
    finally { setBookingActionLoading(false); }
  }

  function startEditBooking() {
    if (!selectedBooking) return;
    setEditBookingForm({
      checkIn: new Date(selectedBooking.checkIn * 1000).toISOString().split('T')[0],
      checkOut: new Date(selectedBooking.checkOut * 1000).toISOString().split('T')[0],
      roomTypeId: selectedBooking.roomTypeId,
      guests: selectedBooking.guests,
      totalAmount: selectedBooking.totalAmount,
    });
    setEditingBooking(true);
  }

  async function saveBookingEdit(id: number) {
    if (!editBookingForm) return;
    setBookingActionLoading(true);
    try {
      await client.api.fetch(`/api/admin/bookings/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          checkIn: new Date(editBookingForm.checkIn).getTime() / 1000,
          checkOut: new Date(editBookingForm.checkOut).getTime() / 1000,
          roomTypeId: Number(editBookingForm.roomTypeId),
          guests: Number(editBookingForm.guests),
          totalAmount: Number(editBookingForm.totalAmount),
        }),
      });
      setEditingBooking(false);
      await fetchBookings();
      await refreshSelectedBooking(id);
    } catch (err) { console.error(err); }
    finally { setBookingActionLoading(false); }
  }

  function exportBookingsCSV() {
    const headers = [t('bookings.columns.id'), t('common.status'), t('bookings.paymentStatusLabel'), t('bookings.supplierStatusLabel'), t('bookings.property'), t('bookings.roomType'), t('common.customerName'), t('common.customerEmail'), t('common.customerPhone'), t('common.checkIn'), t('common.checkOut'), t('bookings.columns.guests'), t('referral.ordersColumns.amount'), t('common.addons'), t('common.createdAt')];
    const rows = bookings.map((b) => {
      const addons = (() => {
        try {
          const arr = JSON.parse(b.addons || '[]');
          return Array.isArray(arr) ? arr.map((a: any) => a.nameZh || a.name).join(' | ') : '';
        } catch { return ''; }
      })();
      return [
        b.id,
        b.status,
        b.paymentStatus,
        b.supplierStatus,
        b.propertyNameZh || b.propertyName || `Property #${b.propertyId}`,
        b.roomTypeNameZh || b.roomTypeName || `Room #${b.roomTypeId}`,
        b.customer?.name || b.customerName || '',
        b.customer?.email || b.customerEmail || '',
        b.customer?.phone || b.customerPhone || '',
        formatDate(b.checkIn),
        formatDate(b.checkOut),
        b.guests,
        b.totalAmount,
        addons,
        formatDateTime(b.createdAt),
      ];
    });
    const csv = [headers, ...rows].map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bookings-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function printBookingConfirmation(id: number) {
    setBookingActionLoading(true);
    try {
      const res = await client.api.fetch(`/api/admin/bookings/${id}`);
      if (!res.ok) throw new Error('Failed to load booking detail');
      const json = await res.json();
      const b = json.data;
      if (!b) return;

      const nights = Math.max(1, Math.round((b.checkOut - b.checkIn) / 86400));
      const addons = (() => {
        try { return JSON.parse(b.addons || '[]'); }
        catch { return []; }
      })();
      const payments = Array.isArray(b.payments) ? b.payments : [];
      const paidAmount = payments
        .filter((p: any) => p.status === 'succeeded' || p.status === 'paid')
        .reduce((sum: number, p: any) => sum + (Number(p.amount) || 0), 0);
      const balance = (Number(b.totalAmount) || 0) - paidAmount;

      const statusMap: Record<string, string> = {
        pending: 'Pending',
        confirmed: 'Confirmed',
        cancelled: 'Cancelled',
        completed: 'Completed',
      };
      const paymentStatusMap: Record<string, string> = {
        unpaid: 'Unpaid',
        partial: 'Partial',
        paid: 'Paid',
        refunded: 'Refunded',
      };
      const supplierStatusMap: Record<string, string> = {
        pending: 'Pending',
        confirmed: 'Confirmed',
        rejected: 'Rejected',
      };
      const paymentMethodMap: Record<string, string> = {
        payme: t('bookings.paymentMethods.payme'),
        fps: 'FPS',
        bank_transfer: 'Bank Transfer',
      };

      const addonRows = addons.length
        ? addons.map((a: any) => `<tr><td>${a.name || a.nameZh || 'Add-on'}</td><td>${a.type === 'retreat' ? 'Retreat' : 'Experience'}</td></tr>`).join('')
        : '<tr><td colspan="2">None</td></tr>';

      const paymentRows = payments.length
        ? payments.map((p: any) => `
            <tr>
              <td>${formatDateTime(p.createdAt || p.created_at)}</td>
              <td>${paymentMethodMap[p.gateway] || p.gateway || t('common.none')}</td>
              <td>${p.gatewayTransactionId || t('common.none')}</td>
              <td>HK$${(Number(p.amount) || 0).toLocaleString()}</td>
              <td>${p.status}</td>
            </tr>
          `).join('')
        : '<tr><td colspan="5">No payment record</td></tr>';

      const w = window.open('', '_blank');
      if (!w) return;
      w.document.write(`
        <html>
          <head>
            <title>Booking Confirmation #${b.id}</title>
            <style>
              body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; color: #222; line-height: 1.5; }
              h1 { font-size: 26px; margin-bottom: 6px; }
              h2 { font-size: 18px; margin: 28px 0 12px; border-bottom: 2px solid #0a4c6b; padding-bottom: 6px; color: #0a4c6b; }
              .subtitle { color: #666; margin-bottom: 24px; }
              .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 32px; }
              .row { display: flex; justify-content: space-between; padding: 7px 0; border-bottom: 1px solid #eee; }
              .row span:first-child { color: #555; }
              table { width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 8px; }
              th, td { text-align: left; padding: 8px; border-bottom: 1px solid #ddd; }
              th { background: #f5f7f8; font-weight: 600; }
              .total { font-size: 18px; font-weight: 700; color: #0a4c6b; }
              .footer { margin-top: 40px; font-size: 12px; color: #888; border-top: 1px solid #eee; padding-top: 12px; }
              @media print { body { padding: 24px; } }
            </style>
          </head>
          <body>
            <h1>HK Stay Islands</h1>
            <p class="subtitle">Booking Confirmation / Supplier Copy</p>

            <h2>Order Reference</h2>
            <div class="grid">
              <div class="row"><span>Booking ID</span><span>#${b.id}</span></div>
              <div class="row"><span>Booking Status</span><span>${statusMap[b.status] || b.status}</span></div>
              <div class="row"><span>Payment Status</span><span>${paymentStatusMap[b.paymentStatus] || b.paymentStatus}</span></div>
              <div class="row"><span>Supplier Status</span><span>${supplierStatusMap[b.supplierStatus] || b.supplierStatus}</span></div>
              ${b.referralCode ? `<div class="row"><span>Referral Code</span><span>${b.referralCode}</span></div>` : ''}
            </div>

            <h2>Guest Information</h2>
            <div class="grid">
              <div class="row"><span>Name</span><span>${b.customer?.name || t('common.none')}</span></div>
              <div class="row"><span>Email</span><span>${b.customer?.email || t('common.none')}</span></div>
              <div class="row"><span>Phone</span><span>${b.customer?.phone || t('common.none')}</span></div>
            </div>

            <h2>Accommodation</h2>
            <div class="grid">
              <div class="row"><span>Property</span><span>${b.property?.name || t('common.none')} ${b.property?.nameZh ? `(${b.property.nameZh})` : ''}</span></div>
              <div class="row"><span>Room Type</span><span>${b.roomType?.name || t('common.none')} ${b.roomType?.nameZh ? `(${b.roomType.nameZh})` : ''}</span></div>
              <div class="row"><span>Check-in</span><span>${formatDate(b.checkIn)}</span></div>
              <div class="row"><span>Check-out</span><span>${formatDate(b.checkOut)}</span></div>
              <div class="row"><span>Nights / Guests</span><span>${nights} nights · ${b.guests} guest(s)</span></div>
            </div>

            <h2>Add-ons</h2>
            <table>
              <thead><tr><th>Item</th><th>Type</th></tr></thead>
              <tbody>${addonRows}</tbody>
            </table>

            <h2>Payment Summary</h2>
            <div class="grid">
              <div class="row"><span>Total Amount</span><span class="total">HK$${(Number(b.totalAmount) || 0).toLocaleString()}</span></div>
              <div class="row"><span>Paid Amount</span><span>HK$${paidAmount.toLocaleString()}</span></div>
              <div class="row"><span>Balance Due</span><span>HK$${Math.max(0, balance).toLocaleString()}</span></div>
              ${b.paymentMethod ? `<div class="row"><span>Payment Method</span><span>${paymentMethodMap[b.paymentMethod] || b.paymentMethod}</span></div>` : ''}
              ${b.paymentReference ? `<div class="row"><span>Payment Reference</span><span>${b.paymentReference}</span></div>` : ''}
            </div>

            <h2>Payment Records</h2>
            <table>
              <thead>
                <tr><th>Date</th><th>Method</th><th>Reference</th><th>Amount</th><th>Status</th></tr>
              </thead>
              <tbody>${paymentRows}</tbody>
            </table>

            ${b.adminNotes ? `
              <h2>Internal Notes</h2>
              <p style="white-space:pre-wrap;background:#f5f7f8;padding:12px;border-radius:6px;">${b.adminNotes}</p>
            ` : ''}

            <div class="footer">
              Generated on ${formatDateTime(new Date())} · HK Stay Islands · For internal and supplier use.
            </div>
            <script>window.print()</script>
          </body>
        </html>
      `);
      w.document.close();
    } catch (err) { console.error(err); }
    finally { setBookingActionLoading(false); }
  }

  async function ensureAndCopyOrderLink() {
    if (!selectedBooking) return;
    let token = selectedBooking.token;
    if (!token) {
      try {
        const res = await client.api.fetch(`/api/admin/bookings/${selectedBooking.id}/token`, { method: 'PATCH' });
        if (!res.ok) throw new Error('Failed to generate token');
        const json = await res.json();
        token = json.data?.token;
        setSelectedBooking({ ...selectedBooking, token });
      } catch (err) {
        alert(t('bookings.generateLinkError'));
        return;
      }
    }
    const link = `${window.location.origin}/order/${token}`;
    try {
      await navigator.clipboard.writeText(link);
      alert(t('bookings.linkCopied', { link }));
    } catch {
      alert(t('bookings.linkFallback', { link }));
    }
  }

  async function addAdmin() {
    setAccountError('');
    setAccountSuccess('');
    if (!newEmail || !newEmail.includes('@')) {
      setAccountError(t('accounts.invalidEmail'));
      return;
    }
    try {
      const res = await client.api.fetch('/api/admin/accounts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newEmail, role: newRole }),
      });
      const data = await res.json();
      if (!res.ok) {
        setAccountError(data.error || t('common.createFailed'));
        return;
      }
      setAccountSuccess(t('accounts.createSuccess', { email: newEmail }));
      setNewEmail('');
      setNewRole('admin');
      fetchAccounts();
    } catch (err) {
      setAccountError(t('accounts.createError'));
    }
  }

  async function deleteAdmin(id: number) {
    setAccountError('');
    setAccountSuccess('');
    try {
      const res = await client.api.fetch(`/api/admin/accounts/${id}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (!res.ok) {
        setAccountError(data.error || t('common.deleteFailed'));
        setDeletingId(null);
        return;
      }
      setAccountSuccess(t('accounts.deleteSuccess'));
      setDeletingId(null);
      fetchAccounts();
    } catch (err) {
      setAccountError(t('accounts.deleteError'));
      setDeletingId(null);
    }
  }

  async function updateAdminRole(id: number, role: string) {
    setAccountError('');
    setAccountSuccess('');
    try {
      const res = await client.api.fetch(`/api/admin/accounts/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
      });
      const data = await res.json();
      if (!res.ok) {
        setAccountError(data.error || t('common.updateFailed'));
        return;
      }
      setAccountSuccess(t('accounts.roleUpdateSuccess'));
      fetchAccounts();
    } catch (err) {
      setAccountError(t('accounts.roleUpdateError'));
    }
  }

  const statusColors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-700',
    confirmed: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-700',
    completed: 'bg-blue-100 text-blue-700',
  };

  const tabs = [
    { key: 'dashboard', label: t('tabs.dashboard') },
    { key: 'bookings', label: t('tabs.bookings') },
    { key: 'leads', label: t('tabs.leads') },
    { key: 'customers', label: t('tabs.customers') },
    { key: 'properties', label: t('tabs.properties') },
    { key: 'experiences', label: t('tabs.experiences') },
    { key: 'packages', label: t('tabs.packages') },
    { key: 'payments', label: t('tabs.payments') },
    { key: 'referral', label: t('tabs.referral') },
    { key: 'whatsapp', label: t('tabs.whatsapp') },
    { key: 'translations', label: t('tabs.translations') },
    ...(adminRole === 'superadmin' ? [{ key: 'accounts', label: t('tabs.accounts') }] : []),
  ];

  // Show spinner while auth state is being restored or if not admin
  if (isChecking || !user || !isAdmin) {
    return (
      <div className="pt-20 pb-16 min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#0a4c6b] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="pt-20 pb-16 min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-[#0d1b2a] mb-2">{t('common.title')}</h1>
        <p className="text-sm text-gray-500 mb-6">{t('common.role', { role: adminRole === 'superadmin' ? t('roles.superadmin') : t('roles.admin') })}</p>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as typeof activeTab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition ${activeTab === tab.key ? 'bg-[#0a4c6b] text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-2 border-[#0a4c6b] border-t-transparent rounded-full" /></div>
        ) : activeTab === 'bookings' ? (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white rounded-2xl shadow-sm p-4 flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">{t('bookings.statusLabel')}</label>
                <select
                  value={bookingStatusFilter}
                  onChange={(e) => { setBookingStatusFilter(e.target.value); setBookingPage(1); }}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                >
                  <option value="">{t('common.all')}</option>
                  <option value="pending">{t('bookings.status.pending')}</option>
                  <option value="confirmed">{t('bookings.supplierStatus.confirmed')}</option>
                  <option value="cancelled">{t('referral.status.cancelled')}</option>
                  <option value="completed">{t('bookings.status.completed')}</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">{t('bookings.paymentStatusLabel')}</label>
                <select
                  value={bookingPaymentFilter}
                  onChange={(e) => { setBookingPaymentFilter(e.target.value); setBookingPage(1); }}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                >
                  <option value="">{t('common.all')}</option>
                  <option value="unpaid">{t('bookings.paymentStatus.unpaid')}</option>
                  <option value="partial">{t('bookings.paymentStatus.partial')}</option>
                  <option value="paid">{t('referral.status.paid')}</option>
                  <option value="refunded">{t('bookings.paymentStatus.refunded')}</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">{t('common.search')}</label>
                <input
                  type="text"
                  value={bookingSearch}
                  onChange={(e) => { setBookingSearch(e.target.value); setBookingPage(1); }}
                  placeholder={t('bookings.searchPlaceholder')}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none w-56"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">{t('bookings.checkInDate')}</label>
                <input
                  type="date"
                  value={bookingDateFrom}
                  onChange={(e) => { setBookingDateFrom(e.target.value); setBookingPage(1); }}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
                <span className="text-gray-400">-</span>
                <input
                  type="date"
                  value={bookingDateTo}
                  onChange={(e) => { setBookingDateTo(e.target.value); setBookingPage(1); }}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <button
                onClick={exportBookingsCSV}
                className="ml-auto text-sm bg-[#B8902F]/10 text-[#B8902F] px-3 py-2 rounded-lg hover:bg-[#B8902F]/20 transition"
              >
                {t('common.exportCsv')}
              </button>
              <div className="text-sm text-gray-500">
                {t('bookings.totalBookings', { count: bookingTotal })}
              </div>
            </div>

            {/* Bookings Table */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">{t('bookings.columns.id')}</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">{t('bookings.columns.customer')}</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">{t('bookings.columns.dates')}</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">{t('bookings.columns.guests')}</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">{t('referral.ordersColumns.amount')}</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">{t('bookings.columns.payment')}</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">{t('common.status')}</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">{t('bookings.columns.supplier')}</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">{t('bookings.columns.referral')}</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {bookings.length === 0 ? (
                      <tr><td colSpan={10} className="px-4 py-12 text-center text-gray-500">{t('bookings.empty')}</td></tr>
                    ) : (
                      bookings.map((b) => (
                        <tr key={b.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono">#{b.id}</td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-[#0d1b2a]">{b.customerName || t('common.none')}</p>
                            <p className="text-xs text-gray-500">{b.customerEmail || ''}</p>
                            <p className="text-xs text-gray-400">{b.customerPhone || ''}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span>{formatDate(b.checkIn)}</span>
                            <span className="text-gray-400 mx-1">→</span>
                            <span>{formatDate(b.checkOut)}</span>
                          </td>
                          <td className="px-4 py-3">{b.guests}</td>
                          <td className="px-4 py-3 font-medium">HK${b.totalAmount?.toLocaleString()}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              b.paymentStatus === 'paid' ? 'bg-green-100 text-green-700' :
                              b.paymentStatus === 'refunded' ? 'bg-blue-100 text-blue-700' :
                              b.paymentStatus === 'partial' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {b.paymentStatus === 'paid' ? t('referral.status.paid') : b.paymentStatus === 'refunded' ? t('bookings.paymentStatus.refunded') : b.paymentStatus === 'partial' ? t('bookings.paymentStatus.partial') : t('bookings.paymentStatus.unpaid')}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[b.status] || 'bg-gray-100'}`}>
                              {b.status === 'pending' ? t('bookings.status.pending') : b.status === 'confirmed' ? t('bookings.supplierStatus.confirmed') : b.status === 'cancelled' ? t('referral.status.cancelled') : t('bookings.status.completed')}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              b.supplierStatus === 'confirmed' ? 'bg-green-100 text-green-700' :
                              b.supplierStatus === 'rejected' ? 'bg-red-100 text-red-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {b.supplierStatus === 'confirmed' ? t('bookings.supplierStatus.confirmed') : b.supplierStatus === 'rejected' ? t('bookings.supplierStatus.rejected') : t('bookings.supplierStatus.pending')}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {b.referralCode ? (
                              <div className="flex flex-col gap-1">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#B8902F]/10 text-[#B8902F] w-fit">
                                  {t('bookings.referral.referral')}
                                </span>
                                <span className="font-mono text-[#0a4c6b] text-xs">{b.referralCode}</span>
                              </div>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 w-fit">
                                {t('bookings.referral.direct')}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => {
                                setSelectedBooking(b);
                                setAdminNotes(b.adminNotes || '');
                              }}
                              className="text-xs bg-[#0a4c6b] text-white px-3 py-1.5 rounded hover:bg-[#083d56] transition"
                            >
                              {t('common.viewDetails')}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {Math.ceil(bookingTotal / bookingLimit) > 1 && (
                <div className="px-4 py-3 border-t flex items-center justify-between">
                  <button
                    onClick={() => setBookingPage((p) => Math.max(1, p - 1))}
                    disabled={bookingPage === 1}
                    className="text-xs bg-gray-100 px-3 py-1.5 rounded disabled:opacity-50"
                  >{t('common.previous')}</button>
                  <span className="text-sm text-gray-600">{t('common.pageOf', { page: bookingPage, total: Math.ceil(bookingTotal / bookingLimit) })}</span>
                  <button
                    onClick={() => setBookingPage((p) => Math.min(Math.ceil(bookingTotal / bookingLimit), p + 1))}
                    disabled={bookingPage === Math.ceil(bookingTotal / bookingLimit)}
                    className="text-xs bg-gray-100 px-3 py-1.5 rounded disabled:opacity-50"
                  >{t('common.next')}</button>
                </div>
              )}
            </div>

            {/* Booking Detail Modal */}
            {selectedBooking && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-[#0d1b2a]">{t('bookings.orderTitle', { id: selectedBooking.id })}</h3>
                    <button
                      onClick={() => setSelectedBooking(null)}
                      className="text-gray-400 hover:text-gray-600 text-2xl"
                      aria-label={t('bookings.close')}
                    >
                      ×
                    </button>
                  </div>

                  <div className="p-6 space-y-6">
                    {/* Status overview */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-gray-50 rounded-xl p-4 text-center">
                        <p className="text-xs text-gray-500 mb-1">{t('bookings.statusLabel')}</p>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[selectedBooking.status] || 'bg-gray-100'}`}>
                          {selectedBooking.status === 'pending' ? t('bookings.status.pending') : selectedBooking.status === 'confirmed' ? t('bookings.supplierStatus.confirmed') : selectedBooking.status === 'cancelled' ? t('referral.status.cancelled') : t('bookings.status.completed')}
                        </span>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4 text-center">
                        <p className="text-xs text-gray-500 mb-1">{t('bookings.paymentStatusLabel')}</p>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          selectedBooking.paymentStatus === 'paid' ? 'bg-green-100 text-green-700' :
                          selectedBooking.paymentStatus === 'refunded' ? 'bg-blue-100 text-blue-700' :
                          selectedBooking.paymentStatus === 'partial' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {selectedBooking.paymentStatus === 'paid' ? t('referral.status.paid') : selectedBooking.paymentStatus === 'refunded' ? t('bookings.paymentStatus.refunded') : selectedBooking.paymentStatus === 'partial' ? t('bookings.paymentStatus.partial') : t('bookings.paymentStatus.unpaid')}
                        </span>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4 text-center">
                        <p className="text-xs text-gray-500 mb-1">{t('bookings.supplierStatusLabel')}</p>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          selectedBooking.supplierStatus === 'confirmed' ? 'bg-green-100 text-green-700' :
                          selectedBooking.supplierStatus === 'rejected' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {selectedBooking.supplierStatus === 'confirmed' ? t('bookings.supplierStatus.confirmed') : selectedBooking.supplierStatus === 'rejected' ? t('bookings.supplierStatus.rejected') : t('bookings.supplierStatus.pending')}
                        </span>
                      </div>
                    </div>

                    {/* Management actions */}
                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        onClick={startEditBooking}
                        disabled={bookingActionLoading}
                        className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg transition disabled:opacity-60"
                      >{t('bookings.editOrder')}</button>
                      <button
                        onClick={() => printBookingConfirmation(selectedBooking.id)}
                        className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg transition"
                      >{t('common.print')}</button>
                      <button
                        onClick={ensureAndCopyOrderLink}
                        className="text-xs bg-[#0a4c6b]/10 hover:bg-[#0a4c6b]/20 text-[#0a4c6b] px-3 py-2 rounded-lg transition"
                      >{t('bookings.copyOrderLink')}</button>
                      <div className="flex items-center gap-2 ml-auto">
                        <span className="text-sm text-gray-600">{t('bookings.supplierStatusLabel')}</span>
                        <select
                          value={selectedBooking.supplierStatus || 'pending'}
                          onChange={(e) => updateSupplierStatus(selectedBooking.id, e.target.value)}
                          disabled={bookingActionLoading}
                          className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm disabled:opacity-60"
                        >
                          <option value="pending">{t('bookings.supplierStatus.pending')}</option>
                          <option value="confirmed">{t('bookings.supplierStatus.confirmed')}</option>
                          <option value="rejected">{t('bookings.supplierStatus.rejected')}</option>
                        </select>
                      </div>
                    </div>

                    {/* Property & room info */}
                    <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                      <h4 className="font-semibold text-[#0d1b2a]">{t('bookings.propertyRoomTitle')}</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">{t('bookings.property')}</p>
                          <p className="font-medium">{selectedBooking.propertyNameZh || selectedBooking.propertyName || t('bookings.fallbackProperty', { id: selectedBooking.propertyId })}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">{t('bookings.roomType')}</p>
                          <p className="font-medium">{selectedBooking.roomTypeNameZh || selectedBooking.roomTypeName || t('bookings.fallbackRoomType', { id: selectedBooking.roomTypeId })}</p>
                        </div>
                      </div>
                    </div>

                    {/* Customer info */}
                    {selectedBooking.customer && (
                      <div className="bg-[#f0f9f7] rounded-xl p-4 space-y-2">
                        <h4 className="font-semibold text-[#0a4c6b]">{t('bookings.customerInfo')}</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">{t('bookings.name')}</p>
                            <p className="font-medium">{selectedBooking.customer.name || t('common.none')}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">{t('bookings.email')}</p>
                            <p className="font-medium">{selectedBooking.customer.email || t('common.none')}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">{t('bookings.phone')}</p>
                            <p className="font-medium">{selectedBooking.customer.phone || t('common.none')}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Referral source */}
                    {selectedBooking.referralCode && (
                      <div className="bg-[#fff8e6] rounded-xl p-4 space-y-2">
                        <h4 className="font-semibold text-[#B8902F]">{t('bookings.referralSource')}</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">{t('bookings.columns.referral')}</p>
                            <p className="font-mono font-medium text-[#0a4c6b]">{selectedBooking.referralCode}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Customer & trip info */}
                    <div className="space-y-3">
                      <h4 className="font-semibold text-[#0d1b2a]">{t('bookings.tripInfo')}</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">{t('bookings.checkInDate')}</p>
                          <p className="font-medium">{formatDate(selectedBooking.checkIn)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">{t('bookings.checkOut')}</p>
                          <p className="font-medium">{formatDate(selectedBooking.checkOut)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">{t('bookings.columns.guests')}</p>
                          <p className="font-medium">{t('bookings.people', { count: selectedBooking.guests })}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">{t('bookings.totalAmount')}</p>
                          <p className="font-medium">HK${selectedBooking.totalAmount?.toLocaleString()}</p>
                        </div>
                        {selectedBooking.paymentDeadline && (
                          <div>
                            <p className="text-gray-500">{t('bookings.paymentDeadline')}</p>
                            <p className="font-medium">{formatDateTime(selectedBooking.paymentDeadline)}</p>
                          </div>
                        )}
                        {selectedBooking.paidAt && (
                          <div>
                            <p className="text-gray-500">{t('bookings.paidAt')}</p>
                            <p className="font-medium">{formatDateTime(selectedBooking.paidAt)}</p>
                          </div>
                        )}
                        {selectedBooking.paymentMethod && (
                          <div>
                            <p className="text-gray-500">{t('bookings.paymentMethod')}</p>
                            <p className="font-medium">
                              {selectedBooking.paymentMethod === 'payme' ? t('bookings.paymentMethods.payme') :
                               selectedBooking.paymentMethod === 'fps' ? t('bookings.paymentMethods.fps') :
                               selectedBooking.paymentMethod === 'bank_transfer' ? t('bookings.paymentMethods.bankTransfer') : selectedBooking.paymentMethod}
                            </p>
                          </div>
                        )}
                        {selectedBooking.paymentReference && (
                          <div>
                            <p className="text-gray-500">{t('bookings.paymentReference')}</p>
                            <p className="font-medium">{selectedBooking.paymentReference}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Addons */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-[#0d1b2a]">{t('bookings.addons.title')}</h4>
                        {bookingAddonsDraft.length > 0 && (
                          <span className="text-xs text-gray-500">{t('bookings.addons.selected', { count: bookingAddonsDraft.length })}</span>
                        )}
                      </div>

                      {bookingLinkedExperiences.length === 0 && bookingLinkedRetreats.length === 0 && !(() => {
                        try { return JSON.parse(selectedBooking.addons || '[]').length; }
                        catch { return 0; }
                      })() && (
                        <p className="text-sm text-gray-400">{t('bookings.addons.noAddons')}</p>
                      )}

                      {bookingLinkedExperiences.length > 0 && (
                        <div className="mb-4">
                          <p className="text-xs font-medium text-gray-600 mb-2">{t('properties.experiences')}</p>
                          <div className="grid grid-cols-1 gap-2">
                            {bookingLinkedExperiences.map((e) => {
                              const checked = bookingAddonsDraft.some((a) => a.type === 'experience' && a.id === e.id);
                              return (
                                <label
                                  key={`exp-${e.id}`}
                                  className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${
                                    checked ? 'border-[#0a4c6b] bg-[#0a4c6b]/5' : 'border-gray-200 hover:border-[#2ec4b6]'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleBookingAddon(e, 'experience')}
                                    className="mt-1"
                                  />
                                  <div className="flex-1">
                                    <p className="font-medium text-sm text-[#0d1b2a]">{e.nameZh}</p>
                                    <p className="text-xs text-gray-500">{e.name} · {e.duration}</p>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {bookingLinkedRetreats.length > 0 && (
                        <div className="mb-4">
                          <p className="text-xs font-medium text-gray-600 mb-2">{t('properties.retreats')}</p>
                          <div className="grid grid-cols-1 gap-2">
                            {bookingLinkedRetreats.map((r) => {
                              const checked = bookingAddonsDraft.some((a) => a.type === 'retreat' && a.id === r.id);
                              return (
                                <label
                                  key={`ret-${r.id}`}
                                  className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${
                                    checked ? 'border-[#0a4c6b] bg-[#0a4c6b]/5' : 'border-gray-200 hover:border-[#2ec4b6]'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleBookingAddon(r, 'retreat')}
                                    className="mt-1"
                                  />
                                  <div className="flex-1">
                                    <p className="font-medium text-sm text-[#0d1b2a]">{r.nameZh}</p>
                                    <p className="text-xs text-gray-500">{r.name} · {r.duration}</p>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {(() => {
                        const legacyAddons = (() => {
                          try { return JSON.parse(selectedBooking.addons || '[]').filter((a: any) => !a.type || !a.id); }
                          catch { return []; }
                        })();
                        if (!legacyAddons.length) return null;
                        return (
                          <div className="mb-4">
                            <p className="text-xs font-medium text-gray-600 mb-2">{t('bookings.addons.legacy')}</p>
                            <div className="space-y-2">
                              {legacyAddons.map((a: any, i: number) => (
                                <div key={i} className="bg-gray-100 rounded-lg px-3 py-2 text-sm">
                                  <p className="font-medium text-[#0a4c6b]">{a.name}</p>
                                  {a.description && <p className="text-gray-600 text-xs mt-0.5">{a.description}</p>}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      {(bookingLinkedExperiences.length > 0 || bookingLinkedRetreats.length > 0) && (
                        <button
                          onClick={() => saveBookingAddons(selectedBooking.id)}
                          disabled={bookingActionLoading}
                          className="text-xs bg-[#0a4c6b] text-white px-3 py-1.5 rounded hover:bg-[#083d56] transition disabled:opacity-60"
                        >
                          {bookingActionLoading ? t('common.saving') : t('bookings.addons.save')}
                        </button>
                      )}
                    </div>

                    {/* Admin notes */}
                    <div>
                      <h4 className="font-semibold text-[#0d1b2a] mb-2">{t('bookings.adminNotes.title')}</h4>
                      <textarea
                        value={adminNotes}
                        onChange={(e) => setAdminNotes(e.target.value)}
                        rows={3}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                        placeholder={t('bookings.adminNotes.placeholder')}
                      />
                      <button
                        onClick={() => saveBookingNotes(selectedBooking.id)}
                        disabled={bookingActionLoading}
                        className="mt-2 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded transition disabled:opacity-60"
                      >
                        {t('bookings.adminNotes.save')}
                      </button>
                    </div>

                    {/* Actions */}
                    {selectedBooking.status !== 'cancelled' && (
                      <div className="border-t pt-6 space-y-6">
                        {selectedBooking.status === 'pending' && (
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => confirmBooking(selectedBooking.id)}
                              disabled={bookingActionLoading}
                              className="bg-green-500 hover:bg-green-600 text-white px-5 py-2 rounded-lg text-sm font-medium transition disabled:opacity-60"
                            >
                              {t('bookings.confirm.title')}
                            </button>
                            <p className="text-xs text-gray-500">{t('bookings.confirm.hint')}</p>
                          </div>
                        )}

                        {selectedBooking.paymentStatus !== 'paid' && selectedBooking.paymentStatus !== 'refunded' && (
                          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                            <h4 className="font-semibold text-[#0d1b2a]">{t('bookings.markPaid.title')}</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <select
                                value={paymentMethod}
                                onChange={(e) => setPaymentMethod(e.target.value)}
                                className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                              >
                                <option value="payme">{t('bookings.paymentMethods.payme')}</option>
                                <option value="fps">{t('bookings.paymentMethods.fps')}</option>
                                <option value="bank_transfer">{t('bookings.paymentMethods.bankTransfer')}</option>
                                <option value="manual">{t('bookings.paymentMethods.manual')}</option>
                              </select>
                              <input
                                type="text"
                                value={paymentReference}
                                onChange={(e) => setPaymentReference(e.target.value)}
                                placeholder={t('bookings.markPaid.referencePlaceholder')}
                                className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                              />
                            </div>
                            <button
                              onClick={() => markBookingPaid(selectedBooking.id)}
                              disabled={bookingActionLoading}
                              className="bg-[#0a4c6b] hover:bg-[#083d56] text-white px-5 py-2 rounded-lg text-sm font-medium transition disabled:opacity-60"
                            >
                              {t('bookings.markPaid.button')}
                            </button>
                          </div>
                        )}

                        <div className="bg-red-50 rounded-xl p-4 space-y-3">
                          <h4 className="font-semibold text-red-700">{t('bookings.cancel.title')}</h4>
                          <p className="text-xs text-red-600">
                            {t('bookings.cancel.hint')}
                          </p>
                          <input
                            type="text"
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            placeholder={t('bookings.cancel.reasonPlaceholder')}
                            className="w-full border border-red-200 rounded-lg px-3 py-2 text-sm"
                          />
                          <button
                            onClick={() => cancelBooking(selectedBooking.id)}
                            disabled={bookingActionLoading}
                            className="bg-red-500 hover:bg-red-600 text-white px-5 py-2 rounded-lg text-sm font-medium transition disabled:opacity-60"
                          >
                            {t('bookings.cancel.button')}
                          </button>
                        </div>
                      </div>
                    )}

                    {selectedBooking.status === 'cancelled' && (
                      <div className="bg-red-50 rounded-xl p-4">
                        <h4 className="font-semibold text-red-700 mb-2">{t('bookings.cancelledInfo.title')}</h4>
                        <p className="text-sm"><span className="text-gray-600">{t('bookings.cancelledInfo.reason')}</span>{selectedBooking.cancellationReason || t('common.none')}</p>
                        <p className="text-sm mt-1"><span className="text-gray-600">{t('bookings.cancelledInfo.refund')}</span>HK${selectedBooking.refundAmount?.toLocaleString()}</p>
                        {selectedBooking.cancelledAt && (
                          <p className="text-sm mt-1"><span className="text-gray-600">{t('bookings.cancelledInfo.time')}</span>{formatDateTime(selectedBooking.cancelledAt)}</p>
                        )}
                      </div>
                    )}

                    {/* Edit booking modal (inline) */}
                    {editingBooking && editBookingForm && (
                      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
                        <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="font-bold text-[#0d1b2a]">{t('bookings.editModal.title', { id: selectedBooking.id })}</h4>
                            <button onClick={() => setEditingBooking(false)} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">{t('bookings.checkInDate')}</label>
                              <input
                                type="date"
                                value={editBookingForm.checkIn}
                                onChange={(e) => setEditBookingForm({ ...editBookingForm, checkIn: e.target.value })}
                                className="w-full border rounded-lg px-3 py-2 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">{t('bookings.checkOut')}</label>
                              <input
                                type="date"
                                value={editBookingForm.checkOut}
                                onChange={(e) => setEditBookingForm({ ...editBookingForm, checkOut: e.target.value })}
                                className="w-full border rounded-lg px-3 py-2 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">{t('bookings.editModal.roomTypeId')}</label>
                              <select
                                value={editBookingForm.roomTypeId}
                                onChange={(e) => setEditBookingForm({ ...editBookingForm, roomTypeId: Number(e.target.value) })}
                                className="w-full border rounded-lg px-3 py-2 text-sm"
                              >
                                {selectedBooking.property?.roomTypes?.map((rt: any) => (
                                  <option key={rt.id} value={rt.id}>{rt.nameZh} (#{rt.id})</option>
                                )) || <option value={editBookingForm.roomTypeId}>{t('bookings.fallbackRoomType', { id: editBookingForm.roomTypeId })}</option>}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">{t('bookings.columns.guests')}</label>
                              <input
                                type="number"
                                value={editBookingForm.guests}
                                onChange={(e) => setEditBookingForm({ ...editBookingForm, guests: Number(e.target.value) })}
                                className="w-full border rounded-lg px-3 py-2 text-sm"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs text-gray-500 mb-1">{t('bookings.editModal.totalAmount')}</label>
                              <input
                                type="number"
                                value={editBookingForm.totalAmount}
                                onChange={(e) => setEditBookingForm({ ...editBookingForm, totalAmount: Number(e.target.value) })}
                                className="w-full border rounded-lg px-3 py-2 text-sm"
                              />
                            </div>
                          </div>
                          <div className="flex justify-end gap-3">
                            <button onClick={() => setEditingBooking(false)} className="text-sm text-gray-500 px-3 py-2">{t('common.cancel')}</button>
                            <button
                              onClick={() => saveBookingEdit(selectedBooking.id)}
                              disabled={bookingActionLoading}
                              className="bg-[#0a4c6b] text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-60"
                            >{t('bookings.editModal.save')}</button>
                          </div>
                        </div>
                      </div>
                    )}


                  </div>
                </div>
              </div>
            )}
          </div>
        ) : activeTab === 'dashboard' ? (
          <DashboardSection />
        ) : activeTab === 'customers' ? (
          <CustomersSection />
        ) : activeTab === 'leads' ? (
          <LeadsSection />
        ) : activeTab === 'payments' ? (
          <PaymentsSection />
        ) : activeTab === 'referral' ? (
          <ReferralSection />
        ) : activeTab === 'whatsapp' ? (
          <WhatsAppTemplatesSection />
        ) : activeTab === 'translations' ? (
          <TranslationsSection />
        ) : activeTab === 'accounts' ? (
          <div className="space-y-6">
            {/* Add new admin form */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-bold text-lg text-[#0d1b2a] mb-4">{t('accounts.addTitle')}</h3>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder={t('accounts.emailPlaceholder')}
                  className="flex-1 border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                />
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value as 'admin' | 'superadmin')}
                  className="border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                >
                  <option value="admin">{t('roles.admin')}</option>
                  <option value="superadmin">{t('roles.superadmin')}</option>
                </select>
                <button
                  onClick={addAdmin}
                  className="bg-[#0a4c6b] text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-[#083d56] transition whitespace-nowrap"
                >
                  {t('common.add')}
                </button>
              </div>
              {accountError && (
                <p className="mt-3 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{accountError}</p>
              )}
              {accountSuccess && (
                <p className="mt-3 text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg">{accountSuccess}</p>
              )}
            </div>

            {/* Admin accounts list */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b bg-gray-50">
                <h3 className="font-bold text-[#0d1b2a]">{t('accounts.listTitle')}</h3>
                <p className="text-xs text-gray-500 mt-1">{t('accounts.totalAccounts', { count: accounts.length })}</p>
              </div>
              <div className="divide-y">
                {accounts.length === 0 ? (
                  <div className="px-6 py-12 text-center text-gray-500">{t('accounts.empty')}</div>
                ) : (
                  accounts.map((account) => (
                    <div key={account.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-[#0a4c6b]/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-medium text-[#0a4c6b]">
                              {account.email.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-[#0d1b2a] truncate">{account.email}</p>
                            <p className="text-xs text-gray-500">
                              {t('accounts.addedAt', { date: formatDate(account.createdAt) })}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 ml-4">
                        <select
                          value={account.role}
                          onChange={(e) => updateAdminRole(account.id, e.target.value)}
                          className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none"
                        >
                          <option value="admin">{t('roles.admin')}</option>
                          <option value="superadmin">{t('roles.superadmin')}</option>
                        </select>
                        {deletingId === account.id ? (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => deleteAdmin(account.id)}
                              className="text-xs bg-red-500 text-white px-3 py-1.5 rounded-lg hover:bg-red-600 transition"
                            >
                              {t('common.confirmDelete')}
                            </button>
                            <button
                              onClick={() => setDeletingId(null)}
                              className="text-xs text-gray-500 hover:text-gray-700"
                            >
                              {t('common.cancel')}
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeletingId(account.id)}
                            className="text-xs text-red-500 hover:text-red-700 px-3 py-1.5 rounded-lg hover:bg-red-50 transition"
                          >
                            {t('accounts.remove')}
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        ) : activeTab === 'experiences' ? (
          <ExperiencesSection />
        ) : activeTab === 'packages' ? (
          <PackagesSection />
        ) : (
          <PropertiesSection />
        )}
      </div>
    </div>
  );
}
